// 批次 1（config 模組）：逐 pageId 的頁面行為註冊表（第三層「業務元件」的雛形位置）。
//
// 為什麼要有這一層：CrudListPage 是通用模板（查詢／新增／修改／刪除／啟停／匯出），
// 但 legacy 的 config 各頁在通用 CRUD 之外還有**頁面級動作與語意**：
//   - annual.jsp:29      刪除連結只在 `row.erasable==1 && row.isEnable==1` 出現
//   - annual.jsp:106-120 是否預設欄：預設列顯示「預設年度」、其餘列是「設為預設年度」連結
//   - annual.jsp:315-360 停用／啟用／刪除前先打 checkAnnualUsageAjax 決定 confirm 文字
//   - role.jsp:32-45     刪除連結只在 `row.id>=1000`（自訂角色）出現
//   - role.jsp:455-480   權限設定欄的「角色設定」連結 → 功能權限設定 modal
//   - location.jsp:250-280 停用／啟用／刪除的 usage check 與專屬文案
//   - department.jsp:640 「新增一級部門」鈕（Stage B）
//
// 這層是**資料驅動的選配掛鉤**，不是第二套頁面架構（§9.5）：模板結構不變，
// 行為只在 `pageBehavior(pageId)` 命中時介入；沒有登錄的 140+ 頁走原本路徑，零影響。
//
// 【證據等級】§7 第 3 級（原始碼）。各條文字/條件附 JSP 行號；confirm 文案逐字取自
// JSP 內的字串字面值（legacy-texts 的 101 條抽樣不含這些頁面級訊息）——【雛形值待實機校準】。

import { useEffect, useMemo, useRef, useState, Fragment, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/base/Button';
import { Checkbox } from '@/components/base/Checkbox';
import { FIELD_BASE_CLASS, LINK_TEXT_CLASS } from '@/components/base/shared-classes';
import { TextInput } from '@/components/base/TextInput';
import { ModalFrame, useDialogs } from '@/components/layouts';
import { t } from '@/messages/zh-TW';
import { MockDataError, legacyContractFieldsFor, optionsFor, resolveLegacyMessageForAction, useDataSource } from '@/mocks';
import { ACCOUNT_ORGANIZATION_OPTIONS } from '@/mocks/resource-rules';
import type { DataSource, Row } from '@/mocks/types';
import { DepartmentTableArea } from '../config/DepartmentTree';
import { LeaveFormDialogModal } from '../leave/LeaveFormDialogModal';
import { FIELD_LABEL_CLASS, FULL_ROW_STYLE } from './LegacyContractFields';
import { LightCalendar } from '../main/dashboard/LightCalendar';
import { RolePermissionModal } from '../config/RolePermissionModal';
import { SyncPeopleModal } from '../config/SyncPeopleModal';
import { AccountV2DialogModal } from '../config/AccountV2DialogModal';
import { AnnualEditModal } from '../config/AnnualEditModal';
import { DepartmentEditModal } from '../config/DepartmentEditModal';
import {
  ClassQueryModalButton,
  ClassQueryStatusCell,
  ClassQueryTitleCell,
} from '../course/ClassQueryModals';
import { ClassEditModal } from '../course/ClassEditModal';
import { CourseStaticListBody } from '../course/CourseStaticListBody';
import { JobTypeBody, JobTypeDepartmentBody } from '../config/ConfigRemainingBodies';
import { AllFormListBody, FormStaticListBody } from '../form/FormQueryBodies';
import { ClassFormListBody } from '../form/ClassFormListBody';
import { CoEditQueryPageBody } from '../form/coEditForm/CoEditQueryPageBody';
import { CoEditFillFormBody } from '../form/coEditForm/CoEditFillFormBody';
import {
  InitAllQuestionBody,
  QuestionManageBody,
  QuestionBankEditQuestionLink,
  ViewTestingBody,
} from '../exam/QuestionPagesBodies';
import { PlanFormBatchSendBody } from '../form/PlanFormBatchSendBody';
import { CommonFormTemplateBody, FormTemplateListBody } from '../formTemplate/FormTemplateListBody';
import { FieldTypeListBody, FormTemplateBody } from '../formTemplate/FormDesignerBodies';
import { CourseQueryTableArea, CourseViewCalendarItem } from '../course/CourseViewModal';
import { CertTemplateBody } from '../customer/tsgh/CustomerDisplayBodies';
import { DispatchExamBody } from '../exam/DispatchExamBody';
import { ExamEditModal } from '../exam/ExamEditModal';
import { PlanInstListBody } from '../training/PlanInstListBody';
import {
  DetailExpanderCell,
  DoneStatusOpener,
  FillChoiceCellOpener,
  StudyRecordDetailRows,
  StudyRecordDraftArea,
  StudyRecordFillToolbar,
  targetLabelOf,
  targetQtyOf,
} from '../training/StudyRecordFlow';
import { TranscriptBody } from '../training/TranscriptBody';
import { ProfileMaintenanceBody } from '../profile/profileMaintenance/ProfileMaintenanceBody';
import { VoteMaintenanceBody } from '../vote/VoteMaintenanceBody';
import { DashboardBody } from '../main/dashboard/DashboardBody';
import { AdminMainBody } from '../main/dashboard/AdminMainBody';
import { DeptAdminBody } from '../main/deptAdmin/DeptAdminBody';
import { DeptManagerRecommendBody } from '../vote/DeptManagerRecommendBody';
import { ExcellentTeacherVoteBody } from '../vote/ExcellentTeacherVoteBody';
import { TotalVoteAnalysisReportBody, TotalVoteReportBody } from '../vote/VoteTotalReportBodies';
import { HighRiskSettingBody } from '../customer/tsgh/HighRiskSettingBody';
import { ServiceStandardSettingBody } from '../customer/tsgh/ServiceStandardSettingBody';
import { PersonalizationGPASettingBody } from '../instruction/PersonalizationGPASettingBody';
import { ArrangeTrainBody } from '../training/ArrangeTrainBody';
import {
  HighRiskDoctorReportBody,
  HighRiskReportBody,
  HighRiskSettingReportBody,
  ServiceStandardCfgReportBody,
  ServiceStandardReportBody,
  TchSatisfactionSurveyReportBody,
  CaseReportBody,
  StudentCourseReportBody,
  CoreCurriculumReportBody,
  CourseRecordReportBody,
} from '../customer/tsgh/CustomerReportBodies';
import { ClertCerkBody, StudyHistoryListBody } from '../customer/tsgh/CustomerDisplayBodies';
import {
  AllStudentScoresBody,
  ClassFormExportBody,
  ClazzAttendanceExportBody,
  ClazzDetailExportBody,
  CoreSkillFormReportBody,
  CoreSkillSutdentReportBody,
  CourseFormFinishrateBody,
  GpaFormExportBody,
  PlanFormExportBody,
  StudentDeptSatisfyBody,
} from '../report/ReportExportBodies';
import {
  ClassFormStatic7Body,
  ClassFormStaticBody,
  CoreSkillReportByFormStatBody,
  CoreSkillReportByStudentStatBody,
  LoginStaticBody,
  PlanFormStatic2Body,
  PlanFormStatic5Body,
  PlanFormStatic9Body,
  PlanFormStaticBody,
  PlanFormStatic10Body,
} from '../report/ReportQueryStatBodies';
import {
  AcademicMomBody,
  CoreSkillStatisticBody,
  CoreSkillStatisticReportBody,
} from '../report/ReportSpecialBodies';
import { toUiKey, type PageColumn } from './shared';

// ---------------------------------------------------------------------------
// 行為掛鉤（CrudListPage 消費）
// ---------------------------------------------------------------------------

/** 行為流程需要的執行環境，由 CrudListPage 注入。 */
export interface BehaviorFlowContext {
  readonly pageId: string;
  readonly reload: () => void;
  /** 批次 12：preset 可攜入口標記（department 樹 editEntry='tree'）／新增 preset（parentId 等）。 */
  readonly openEdit: (row: Row, preset?: Record<string, string>) => void;
  readonly openCreate: (preset?: Record<string, string>) => void;
  readonly dataSource: DataSource;
  readonly dialogs: ReturnType<typeof useDialogs>;
  /** 批量勾選狀態（帳號維護的停用/啟用勾選欄）；行為層未宣告 selectionBuckets 時為 undefined。 */
  readonly selection?: BehaviorSelectionApi;
  /** 批次 4：列的 done 旗標查詢（表單查詢雙頁的批量流程用）；未提供＝undefined。 */
  readonly doneLookup?: (id: string) => boolean;
  /** 批次 4 複驗 F2：列的 enabled 旗標查詢（啟停二次檢核用）。 */
  readonly enabledLookup?: (id: string) => boolean;
  /** 批次 4：本頁目前資料列的 id 清單（全選用）。 */
  readonly allRowIds?: () => readonly string[];
  /** 批次 9：查詢表單草稿值（CrudListPage 的 draftFieldValues）；匯入流程讀 org 選單與 comment 的契約檔案欄。 */
  readonly queryValues?: Readonly<Record<string, string>>;
  /** 批次 15（B7-1）：目前展開的列鍵集合（legacy DataTable row.child；planInstList 明細）。 */
  readonly expandedRowIds?: ReadonlySet<string>;
  /** 批次 15：切換某列的展開狀態（明細 details-control 點擊，planInstList_js.jsp:133-145）。 */
  readonly toggleRowExpand?: (rowId: string) => void;
  /** 批次 15 Wave 2（B7-2）：依列鍵取列（fillCross 對勾選列做驗證；studyRecordFill.jsp:29-62）。 */
  readonly rowsById?: (id: string) => Row | undefined;
  /** 批次 22（B2-2）：把值寫回查詢表單草稿並以合併後的 filters 重查（viewRender 鏡像：
   *  月檢視起訖寫回 #beginDate/#endDate 後 queryClassAndSetCalendar，clazzList_js.jsp:2862-2868）。 */
  readonly applyQueryValues?: (values: Record<string, string>) => void;
  /** 批次 22（B2-2）：查詢鈕版本號（每次 applySearch 遞增）。legacy #btnQuery 把 #display 設回
   *  table（clazzList_js.jsp:165-168），行事曆檢視中按「查詢」要切回列表。 */
  readonly searchVersion?: number;
}

/** 批量勾選狀態（accountV2.jsp:19-23 的 checkDisable／checkEnable checkbox）。 */
export interface BehaviorSelectionApi {
  has(bucket: string, id: string): boolean;
  toggle(bucket: string, id: string): void;
  ids(bucket: string): readonly string[];
  clear(bucket?: string): void;
}

/** 「是否已被使用」檢查的 confirm／alert 文字（逐字取自各 JSP）。 */
export interface UsageCheckTexts {
  /** 停用確認：已被使用版。 */
  readonly disableUsed: string;
  /** 停用確認：未被使用版。 */
  readonly disableUnused: string;
  /** 啟用確認（legacy 啟用不做 usage check，直接 confirm）。 */
  readonly enable: string;
  /** 刪除前 usage check 失敗的 alert。 */
  readonly deleteUsed: string;
  /** 刪除確認文字覆寫（courseType.jsp:299『將刪除此類別…』）；未給＝通用 t('confirm.delete')。 */
  readonly deleteConfirm?: string;
}

export interface PageBehavior {
  /** 工具列「新增」鈕文字覆寫（department：新增一級部門）。 */
  readonly addLabel?: string;
  /** 一般刪除流程的 confirm 文字覆寫（clazzList.jsp:366『是否確定要刪除？』全形問號）；未給＝通用。
   *  可帶函式依列動態（questionBank.jsp:365-368：題庫內尚有題目時換確認文字）。 */
  readonly deleteConfirm?: string | ((row: Row) => string);
  /** 這幾欄的儲存格包成「修改」連結（legacy `<a class="showEdit">`）。 */
  readonly editLinkKeys?: readonly string[];
  /** 操作欄不放「修改」鈕（legacy 操作欄只有刪除；修改掛在名稱連結上）。 */
  readonly suppressRowEdit?: boolean;
  /** 「刪除」鈕顯示條件；未登錄＝一律顯示（現有行為）。 */
  readonly deleteVisible?: (row: Row) => boolean;
  /** 「啟用/停用」切換鈕顯示條件（掛在操作欄的那顆）。 */
  readonly toggleVisible?: (row: Row) => boolean;
  /** 刪除／停用前先打 usage check（dataSource.get → `usageUsed`），文字逐字取自 JSP。 */
  readonly usageCheck?: UsageCheckTexts;
  /** 資料欄儲存格覆寫（回 null/undefined 走預設渲染）。 */
  readonly cellOverride?: (row: Row, columnKey: string, ctx: BehaviorFlowContext) => ReactElement | null;
  /** modal 表單欄位調整（annual：年度／系統預設年度／排序序號，去掉狀態欄）。 */
  readonly modalColumns?: (columns: readonly PageColumn[], mode: "create" | "edit") => readonly PageColumn[];
  /** modal 標題覆寫（legacy 是「新增／修改＋功能名」之外的專屬組合，例：新增年度）。 */
  readonly modalTitle?: (
    mode: 'create' | 'edit',
    row: Row | null,
    values?: Record<string, string>,
  ) => string;
  /** 這一列的 modal 是否唯讀（annual.jsp:56-63：停用列隱藏存檔、欄位 disabled）。 */
  readonly modalReadOnly?: (row: Row) => boolean;
  /**
   * crud-fix（2026-10-04，P35 反轉）：id（編號）欄解除唯讀（FieldControl 的 allowIdEdit）。
   * config-job-title 的職稱代碼欄 legacy showModal 新增/修改皆可填（jobTitle.jsp:12/:23
   * `$('#code').val(...)`；save() :126-131 code required）。未登錄＝id 欄維持唯讀
   * （courseDepartmentCode.jsp:108 模板語意）。
   */
  readonly modalIdEditable?: (columnKey: string) => boolean;
  /**
   * crud-fix（2026-10-04，P36 反轉）：modal 個別欄位的 disabled（Select 對應 readOnly）。
   * config-job-title 修改 modal 的醫院欄 legacy 回填後 disabled（jobTitle.jsp showModal
   * `$("#dialogForm #organizationId").attr("disabled", true)`）。未登錄＝不鎖。
   */
  readonly modalFieldDisabled?: (columnKey: string, mode: 'create' | 'edit') => boolean;
  /**
   * crud-fix（2026-10-04）：存檔成功後的第二段 modal（qb 兩段式誠實殼）。
   * legacy questionBank.jsp nextBtn=save(0) 成功 → showInitImportModal()（標題「新增題目」，
   * 匯入/手動兩選）；React 誠實殼＝存檔成功提示後開同標題唯讀殼 modal（關閉鈕收尾；
   * 真題目維護＝#19 範疇）。mode='edit' 不回（legacy 只有新增模式走 nextBtn，P29）。
   */
  readonly postSaveModal?: (input: {
    mode: 'create' | 'edit';
    id: string;
    row: Row | null;
  }) => { title: string; note: string } | undefined;
  /** 包住資料表區塊（department：樹狀圖/列表頁籤容器）；children 為 DataTableShell，rows 為本頁資料列。 */
  readonly tableArea?: (children: ReactElement, ctx: BehaviorFlowContext, rows: readonly Row[]) => ReactElement;
  /**
   * 批次 15（B7-1，Gitea issue #13）：列展開的子內容（legacy DataTable `row.child()`；
   * planInstList 明細 details-control，planInstList_js.jsp:133-145/417-531；
   * 學習紀錄 fill/status 已填明細同款）。受控：CrudListPage 持展開狀態，
   * 展開控制項由 cellOverride 自行畫（ctx.toggleRowExpand）。未登錄頁零影響。
   */
  readonly rowExpanded?: (row: Row, ctx: BehaviorFlowContext) => ReactElement | null;
  /** 宣告批量勾選桶（accountV2：disable／enable）；宣告後 CrudListPage 提供 selection API。 */
  readonly selectionBuckets?: readonly string[];
  /** 工具列額外動作（帳號維護：停用帳號/啟用帳號/清除被封鎖IP/人事同步/批次上傳頭像）。 */
  readonly bulkActions?: (ctx: BehaviorFlowContext) => ReactElement;
  /**
   * 批次 32（CFG-001，issue #33）：匯入區塊（ImportToolbar）專屬 slot——渲染在查詢表單
   * 之後、表格之前（root 層）。legacy 的匯入區塊（label＋input file＋檔案匯入＋使用說明＋
   * 範本下載＋ul）在查詢 form **之外**（config-annual/organization.jsp raw-dom；config 群 7 頁同構），
   * 批次 9 誤掛 bulkActions（查詢 form 內）＝R:form 12 筆差。
   */
  readonly importBlock?: (ctx: BehaviorFlowContext) => ReactElement;
  /** 批次 39（GNL-005-c，issue #39）：匯入區塊在查詢 form **內**（clazzList.jsp:152-170
   *  在 queryForm 內，與 config 群的 root 層不同）；渲染在 SearchPanel fields 後。 */
  readonly importBlockInForm?: (ctx: BehaviorFlowContext) => ReactElement;
  /**
   * 批次 43（#41）：查詢 form **頂部**（fields 之前）的工具列（studyRecordFill.jsp:243-247
   * 一次填寫多類鈕＋說明文字＝form[1] 前兩個子節點；原 bulkActions 位置在查詢鈕前列＝順序差）。
   */
  readonly searchTopActions?: (ctx: BehaviorFlowContext) => ReactElement;
  /**
   * 批次 44（#42）：查詢鈕**之後**的按鈕列（planFormList.jsp:709-721：查詢/列印/啟用/停用/
   * 刪除/變更目前填寫者/變更為未完成/通知填寫＝查詢鈕後同一排）。未登錄＝零影響。
   */
  readonly searchTailActions?: (ctx: BehaviorFlowContext) => ReactElement;
  /**
   * 批次 44（#42）：契約欄位渲染排除（form-plan-form-list 的 ids 屬 printForm 表單，
   * 不在查詢 form＝批次 41 ClassFormListBody 前例；契約面保留＝payload 照帶）。 */
  readonly queryFieldExclusions?: readonly string[];
  /**
   * 批次 45（#10 B4-1，issue #10）：名稱連結的導流 URL（formList.js:113-122 showFillForm
   * ＝window.open form/prompt/fillForm?formKey=…；參數相依頁的進入點；站內導航鏡像＝
   * B19-2 先例）。未登錄＝連結走既有 openEdit（modal）。
   */
  readonly linkNavigate?: (row: Row) => string;
  /**
   * 批次 44（#42）：欄位控制項之後的額外節點（planFormList.jsp:639/:654 reset 圓鈕）。
   * 鍵＝契約欄位 name；onChange 只寫草稿（清除語意，查詢按鈕才生效）。
   */
  readonly queryInlineExtras?: (
    ctx: BehaviorFlowContext,
    onChange: (name: string, next: string) => void,
  ) => Readonly<Record<string, ReactElement>>;
  /**
   * 批次 45（#11 B5-2）：contract modal 開啟時的欄位播種（leave-annual students hidden
   * ＝表格學員勾選）。鍵＝欄位 name。
   */
  readonly modalFieldSeed?: (ctx: BehaviorFlowContext) => Record<string, string>;
  /**
   * 批次 45（#11 B5-2）：contract modal 送出前的驗證鏈（leaveAnnual.jsp:136-165 逐條；
   * 回訊息字串＝alert 攔下；null＝通過送出）。
   */
  readonly modalSubmitGuard?: (
    spec: import('@/mocks/legacy-modals.generated').LegacyModalSpec,
    payload: Record<string, string>,
    ctx: BehaviorFlowContext,
  ) => string | null;
  /**
   * 批次 44（#42）：查詢 form 內、契約欄位**之後**的尾部欄位（comment.jsp:86-92：常用語：
   * label＋selePrivate checkbox＋(只顯示自已的常用語內容)；契約無此欄＝行為層補）。
   * onChange 只寫草稿（不即查）——comment 的 selePrivate 是「下次查詢才生效」的過濾。
   */
  readonly queryTailFields?: (
    ctx: BehaviorFlowContext,
    onChange: (name: string, next: string) => void,
  ) => ReactElement;
  /**
   * 批次 44（#42）：表頭第一欄（空字串欄）畫全選 checkbox（leaveAnnual.jsp／planFormList.jsp:741
   * id=form-select-all／exam dispatch）。true＝裸 checkbox；字串＝帶該 id。未登錄＝既有行為。
   */
  readonly theadSelectAll?: boolean | string;
  /**
   * W11（批次 42，issue #40，裁決④案 B）：查詢 form 內、契約欄位**之前**的額外欄位
   * （organization 組織類型：queryType 無 name＝observed 抽不到、契約無此欄，
   * 契約凍結面零觸碰——structure 由 extraFields 補齊）。受控值由 ctx.queryValues 取
   * （ CrudListPage 的 draftFieldValues）；onChange 寫草稿＋立即套用查詢（legacy
   * onchange="doQuery();"，organization.jsp:348／:124 送 type 參數）。
   */
  readonly queryExtraFields?: (
    ctx: BehaviorFlowContext,
    onChange: (name: string, next: string) => void,
  ) => ReactElement;
  /**
   * 批次 11（B2-3）：行事曆項目的自訂渲染（課程查詢：活動點擊＝CourseModal 聚合檢視，
   * clazzQueryList.js:1177；開課管理預設仍為 openEdit→showClass）。未登錄＝原按鈕。
   */
  readonly calendarItemRenderer?: (row: Row, ctx: BehaviorFlowContext) => ReactElement;
  /** 額外列動作（開課管理：複製），畫在操作欄「刪除」之後。 */
  readonly rowExtraActions?: (row: Row, ctx: BehaviorFlowContext) => ReactElement[];
  /**
   * 批次 10（B1-2）：以自訂 modal 取代通用新增/修改 modal（帳號大 modal）；未登錄頁零影響。
   * onClose＝關 modal（CrudListPage setEditing(null)）；onClosed＝關閉後刷新清單
   * （legacy hidden.bs.modal → doQuery，accountV2.jsp:442-448）。
   * 批次 12（B1-4）：補傳 `values`（＝editing.values）——樹「新增子部門」經 openCreate 帶
   * parentId preset（department.jsp:62），自訂 modal 要看得到它才能判斷層級。
   */
  readonly editModal?: (props: {
    pageId: string;
    mode: 'create' | 'edit';
    row: Row | null;
    values: Record<string, string>;
    onClose: () => void;
    onClosed: () => void;
  }) => ReactElement;
  /** 批次 10：editModal 開啟的 modal uiId（D-3 自報；工具列「新增」鈕的 data-modal-trigger 用；
   *  未登錄＝`${pageId}-form-dialog`）。 */
  readonly editModalTriggerId?: string;
  /** 批次 43（#41 GNL-005-e，裁決③）：legacy 說明區含互動件（formGenerator.jsp:162-182 編輯說明/
   *  儲存鈕＋p×2＋desc-textarea）時用此 hook 自訂；純文字說明區走 legacy-aside-notes 地圖。 */
  readonly asideNote?: (props: { pageId: string }) => ReactElement;
  /**
   * 批次 13（B3-3）：complex-form 頁型（ComplexFormPage）的整頁自訂 body。
   * ComplexFormPage 原本是「單筆表單」模板（無表格、無行為掛鉤）——試卷派送頁在 legacy 是
   * 查詢表單＋listTable＋分派 modal，需要整頁置換。未登錄頁零影響（editModal 同款 opt-in）。
   */
  readonly complexBody?: (props: { pageId: string }) => ReactElement;
  /** 批次 13：complexBody 頁的「送出」鈕自報（C5-h 接線率測試以它為 submit；
   *  試卷派送＝查詢鈕，值經 list filters 進 DataSource）。 */
  readonly complexBodySubmitId?: string;
  /**
   * 批次 14（Gitea issue #22）：report 頁型（ReportPage）的整頁自訂 body。
   * ReportPage 的通用渲染（查詢＋通用 fixture 表格）與 report 模組 23 頁的實際形狀
   * （查詢表單＋匯出鈕驗證鏈，多頁無結果表；查詢統計頁的固定表頭表格；academicMOM 的
   * 基準數 modal）不同，需要整頁置換。未登錄頁零影響（complexBody 同款 opt-in）。
   */
  readonly reportBody?: (props: { pageId: string }) => ReactElement;
  /** 批次 14：reportBody 頁的「送出」鈕自報（C5-h 接線率測試以它為 submit；
   *  匯出頁＝匯出鈕，值經 list filters 進 DataSource）。 */
  readonly reportBodySubmitId?: string;
  /**
   * 批次 19（B8-1，Gitea issue #14）：dashboard 頁型（main-* 首頁 8 變體）的整頁自訂 body。
   * 契約 0 欄（contracts/pages/main-*.json）——非契約驅動頁，結構/行為以 legacy JSP＋JS 為
   * 基準（執行文件 docs/plans/2026-09-27-batch19-main-dashboard-plan.md）。未登錄頁零影響
   * （complexBody 同款 opt-in）。
   */
  readonly dashboardBody?: (props: { pageId: string }) => ReactElement;
  /**
   * 批次 20（R20-2）：unresolved 頁型（UnresolvedPage）的整頁自訂 body。
   * main-medical-affairs-clinical-main（B19-1）＝legacy clinicalMain.jsp（orphan：無 controller/menu
   * 參照＝legacy 實際 404；R20-2 以「完整原始碼存在」鏡像其畫面行為為第 9 dashboard 變體）。
   * 未登錄頁零影響（dashboardBody 同款 opt-in）。 UnresolvedPage 早退。
   */
  readonly unresolvedBody?: (props: { pageId: string }) => ReactElement;
}

// ---------------------------------------------------------------------------
// 共用流程（usage check／預設年度）
// ---------------------------------------------------------------------------

/** row 的「已被使用」旗標（resource-rules 種子補欄；legacy 由 checkXxxUsageAjax 回 isUsageCount/isUsed）。 */
function usageUsedOf(row: Row): boolean {
  return String(row['usageUsed'] ?? '') === '1';
}

/** 批次 1：刪除前的 usage check 流程（annual.jsp:312-331／department.jsp:464-483／location.jsp:250-273）。 */
export async function deleteWithUsageCheck(
  ctx: BehaviorFlowContext,
  row: Row,
  texts: UsageCheckTexts,
): Promise<void> {
  const { pageId, dataSource, dialogs, reload } = ctx;
  let target: Row = row;
  try {
    // legacy 先打 checkXxxUsageAjax；雛形以 get() 取得同一列的最新 usage 旗標。
    const fetched = await dialogs.processing.wrap(dataSource.get(pageId, String(row.id ?? '')));
    if (fetched !== null) {
      target = fetched;
    }
  } catch {
    // usage check 打不到就視同未被使用，讓使用者至少能走到 confirm（原系統此時會 alert desc）。
  }
  if (usageUsedOf(target)) {
    await dialogs.alert(texts.deleteUsed);
    return;
  }
  const confirmed = await dialogs.confirm(texts.deleteConfirm ?? t('confirm.delete'));
  if (!confirmed) {
    return;
  }
  try {
    await dialogs.processing.wrap(dataSource.remove(pageId, String(row.id ?? '')));
    reload();
  } catch (cause: unknown) {
    await dialogs.alert(legacyAlertFallback(cause));
  }
}

/**
 * 批次 1：啟用／停用切換流程（含 usage check）。
 * legacy：停用 → checkXxxUsageAjax → 依 isUsageCount 選 confirm 文字 → changeXxxEnableTypeAjax；
 * 啟用 → 直接 confirm『是否確定要啟用?』。雛形一律走 update()（語意：改狀態欄）。
 */
export async function toggleWithUsageCheck(
  ctx: BehaviorFlowContext,
  row: Row,
  texts: UsageCheckTexts,
  columnKey: string,
  target: "enable" | "disable",
): Promise<void> {
  const { pageId, dataSource, dialogs, reload } = ctx;
  const nextValue = target === 'enable' ? '啟用' : '停用';
  if (String(row[columnKey] ?? '') === nextValue) {
    return; // 已是目標狀態（legacy 的連結本來就只顯示相反方向，防禦性檢查）。
  }
  let confirmText = texts.enable;
  if (target === 'disable') {
    let current: Row = row;
    try {
      const fetched = await dialogs.processing.wrap(dataSource.get(pageId, String(row.id ?? '')));
      if (fetched !== null) {
        current = fetched;
      }
    } catch {
      // 同 deleteWithUsageCheck：取不到旗標就當未被使用。
    }
    confirmText = usageUsedOf(current) ? texts.disableUsed : texts.disableUnused;
  }
  const confirmed = await dialogs.confirm(confirmText);
  if (!confirmed) {
    return;
  }
  try {
    await dialogs.processing.wrap(dataSource.update(pageId, String(row.id ?? ''), { [columnKey]: nextValue }));
    reload();
    await dialogs.info(t('state.saveSuccess'));
  } catch (cause: unknown) {
    await dialogs.alert(legacyAlertFallback(cause));
  }
}

/** 業務規則層丟出的 legacy 原文優先，罐頭訊息與無訊息時退回通用文案。 */
const CANNED_MESSAGES: ReadonlySet<string> = new Set([
  '資料載入失敗，請稍後再試。',
  '您沒有權限存取此功能',
  '找不到指定的資料。',
  '欄位驗證未通過，請檢查標示的欄位。',
]);

export function legacyAlertFallback(cause: unknown): string {
  return cause instanceof MockDataError && cause.message !== '' && !CANNED_MESSAGES.has(cause.message)
    ? cause.message
    : t('error.generic');
}

// ---------------------------------------------------------------------------
// cell 元件（cellOverride 用；自己掛 hooks，不用逐層傳 state）
// ---------------------------------------------------------------------------

/**
 * annual「是否預設」欄（annual.jsp:106-120）：預設列顯示「預設年度」文字，
 * 其餘列顯示「設為預設年度」連結——confirm『是否設為預設年度?』後
 * update `{status:'是'}`，resource-rules 的副作用把其他列改成「否」。
 */
function AnnualDefaultCell({ row, ctx }: { row: Row; ctx: BehaviorFlowContext }) {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const [busy, setBusy] = useState(false);
  const key = String(row.id ?? '');
  const rowKey = toUiKey(key);
  if (String(row['status'] ?? '') === '是') {
    return <span data-ui-id={`${ctx.pageId}-row-${rowKey}-default-annual`}>預設年度</span>;
  }
  return (
    <Button
      uiId={`${ctx.pageId}-row-${rowKey}-set-default-annual`}
      variant="default"
      size="sm"
      type="button"
      disabled={busy}
      // 語意是「更新這筆的是否預設欄位」，走既有 update()，D-1 第 1 點的五個 op 之內。
      dataEffect={`update:${ctx.pageId}`}
      onClick={() => {
        void (async () => {
          setBusy(true);
          try {
            const confirmed = await dialogs.confirm('是否設為預設年度?');
            if (!confirmed) {
              return;
            }
            await dialogs.processing.wrap(dataSource.update(ctx.pageId, key, { status: '是' }));
            ctx.reload();
            await dialogs.info(t('state.saveSuccess'));
          } catch (cause: unknown) {
            await dialogs.alert(legacyAlertFallback(cause));
          } finally {
            setBusy(false);
          }
        })();
      }}
    >
      設為預設年度
    </Button>
  );
}

/**
 * 狀態欄的啟用／停用切換連結（annual.jsp:126-144／department.jsp:538-553／
 * location.jsp 對應 render）：值文字＋相反方向的連結，連結只在前端權限/erasable
 * 條件成立時出現（`canToggle(row)` 由各頁行為提供）。
 */
export function StatusToggleCell({
  row,
  columnKey,
  ctx,
  texts,
  canToggle,
}: {
  row: Row;
  columnKey: string;
  ctx: BehaviorFlowContext;
  texts: UsageCheckTexts;
  canToggle(row: Row): boolean;
}) {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const enabled = String(row[columnKey] ?? '') === '啟用';
  const flowCtx: BehaviorFlowContext = { ...ctx, dataSource, dialogs };
  const rowKey = toUiKey(String(row.id ?? ''));
  return (
    <span data-ui-id={`${ctx.pageId}-row-${rowKey}-status-cell`} className="inline-flex items-center gap-2">
      {enabled ? (
        <span>啟用</span>
      ) : (
        <span className="text-danger">停用</span>
      )}
      {canToggle(row) ? (
        <Button
          uiId={`${ctx.pageId}-row-${rowKey}-toggle-status`}
          variant="default"
          size="sm"
          type="button"
          // 語意是「更新這筆資料的狀態欄」，走既有 update()（D-1 五個 op 之內）。
          dataEffect={`update:${ctx.pageId}`}
          onClick={() => {
            void toggleWithUsageCheck(flowCtx, row, texts, columnKey, enabled ? "disable" : "enable");
          }}
        >
          {enabled ? t('action.disable') : t('action.enable')}
        </Button>
      ) : null}
    </span>
  );
}

// ---------------------------------------------------------------------------
// 註冊表
// ---------------------------------------------------------------------------

/** config-annual（年度維護，origin annual.jsp）。 */
const ANNUAL_BEHAVIOR: PageBehavior = {
  suppressRowEdit: true, // annual.jsp:33-56：操作欄只有刪除，修改掛在年度連結（.showEdit）
  // 批次 44（#42）：表頭全選（leaveAnnual.jsp thead 第一欄 input[type=checkbox]）。
  theadSelectAll: true,
  editLinkKeys: ['date'], // 年度欄＝.showEdit 連結（annual.jsp:57-61）→ 開修改 modal
  deleteVisible: (row) => String(row['erasable'] ?? '') === '1' && String(row['status2'] ?? '') === '啟用',
  toggleVisible: () => false, // 切換連結改畫在狀態欄（cellOverride），操作欄不放
  usageCheck: {
    disableUsed: '年度已被使用，停用後將不可再使用此年度，是否確定要停用?', // annual.jsp:312
    disableUnused: '停用後將不可再使用此年度，是否確定要停用?', // annual.jsp:319
    enable: '是否確定要啟用?', // annual.jsp:330
    deleteUsed: '年度已被使用，不能刪除，請改成停用', // annual.jsp:345
  },
  cellOverride: (row, columnKey, ctx) => {
    if (columnKey === 'status') {
      return <AnnualDefaultCell row={row} ctx={ctx} />;
    }
    if (columnKey === 'status2') {
      return (
        <StatusToggleCell
          row={row}
          columnKey={columnKey}
          ctx={ctx}
          texts={ANNUAL_BEHAVIOR.usageCheck as UsageCheckTexts}
          canToggle={() => true}
        />
      );
    }
    return null;
  },
  modalColumns: (columns) =>
    // annual.jsp:441-459 modal 欄位：年度／系統預設年度(是/否 radio)／排序序號——沒有狀態欄。
    // （批次 12 B1-5 起 modal 由 AnnualEditModal 接管，此處僅供 openCreate/openEdit 的值初始化。）
    columns
      .filter((column) => column.key !== 'status2')
      .map((column) => {
        if (column.key === 'date') {
          return { ...column, label: '年度', kind: 'text' as const };
        }
        if (column.key === 'status') {
          return { ...column, label: '系統預設年度', kind: 'status' as const, pool: ['是', '否'] };
        }
        if (column.key === 'score') {
          return { ...column, label: '排序序號' };
        }
        return column;
      }),
  modalTitle: (mode) => (mode === 'create' ? '新增年度' : '修改年度'), // annual.jsp:66,79
  modalReadOnly: (row) => String(row['status2'] ?? '') === '停用', // annual.jsp:56-63
  // 批次 12（B1-5）：年度編輯 modal（#dialogModal #dialogForm，annual.jsp:440-474）——
  // 系統預設年度改回 legacy 的 radio 是/否（:450-451）、停用列只鎖年度/排序＋藏存檔
  // （:95-101）；通用模板 modal 不再使用（批次 10 editModal hook 先例）。
  editModal: (props) => (
    <AnnualEditModal
      pageId={props.pageId}
      mode={props.mode}
      row={props.row}
      onClose={props.onClose}
      onClosed={props.onClosed}
    />
  ),
  importBlock: (ctx) => <ImportToolbar ctx={ctx} spec={importSpecFor('config-annual')} />, // 批次 9：匯入年度資料（annual.jsp:252-305／:388-404）；批次 32 移 root 層（CFG-001）
};

/** config-department 的 usage check／confirm 文字（department.jsp:451-483）。 */
const DEPARTMENT_TEXTS: UsageCheckTexts = {
  disableUsed: '部門已被使用，停用後將不可再使用此部門，是否確定要停用?', // department.jsp:458
  disableUnused: '停用後將不可再使用此部門，是否確定要停用?', // department.jsp:462
  enable: '是否確定要啟用?', // department.jsp:471
  deleteUsed: '部門已被使用，不能刪除，請改成停用', // department.jsp:473
};

/** config-department（部門維護，origin department.jsp）。 */
const DEPARTMENT_BEHAVIOR: PageBehavior = {
  addLabel: '新增一級部門', // department.jsp:576
  suppressRowEdit: true, // 列表操作欄只有刪除（department.jsp:525-533）；修改掛在部門名稱連結
  editLinkKeys: ['unit'], // 部門名稱欄＝.showEdit（department.jsp:534-537）
  deleteVisible: (row) =>
    String(row['erasable'] ?? '') === '1' && String(row['status'] ?? '') === '啟用', // department.jsp:526
  toggleVisible: () => false, // 切換連結畫在狀態欄（cellOverride）
  usageCheck: DEPARTMENT_TEXTS,
  cellOverride: (row, columnKey, ctx) => {
    if (columnKey === 'status') {
      return (
        <StatusToggleCell
          row={row}
          columnKey={columnKey}
          ctx={ctx}
          texts={DEPARTMENT_TEXTS}
          // department.jsp:538-553：啟用/停用連結掛在 isPermitDelete && row.erasable==1 下。
          canToggle={(target) => String(target['erasable'] ?? '') === '1'}
        />
      );
    }
    return null;
  },
  modalColumns: (columns) => {
    // department.jsp:683-738 modal 欄位：醫院/父部門（依層級擇一，雛形暫不畫，見進度檔
    // 已知差異）＋部門代碼/部門名稱/部門簡稱/排序序號（＋異動人員/日期，暫不畫）。
    // （批次 12 B1-4 起 modal 由 DepartmentEditModal 接管，此處僅供 openCreate/openEdit 的值初始化。）
    const out: PageColumn[] = [];
    for (const column of columns) {
      if (column.key === 'id') {
        out.push({ ...column, label: '部門代碼', kind: 'text' as const });
      } else if (column.key === 'unit') {
        out.push({ ...column, label: '部門名稱', kind: 'text' as const });
      } else if (column.key === 'score') {
        out.push({ ...column, label: '排序序號' });
      }
    }
    out.push({ key: 'shortName', label: '部門簡稱', kind: 'text' as const });
    return out;
  },
  importBlock: (ctx) => <ImportToolbar ctx={ctx} spec={importSpecFor('config-department')} />, // 批次 9：匯入部門資料（department.jsp:386-432／:614-628）；批次 32 移 root 層（CFG-001）
  // 批次 12（B1-4）：部門編輯 modal（#dialogModal #itemForm，department.jsp:676-750）——
  // 四態層級擇一（醫院/父部門，showModal :41-136／showTableModal :131-190）＋異動人員/日期＋
  // 請選擇醫院檢核（:336-338）；通用模板 modal 不再使用（批次 10 editModal hook 先例）。
  editModal: (props) => (
    <DepartmentEditModal
      pageId={props.pageId}
      mode={props.mode}
      row={props.row}
      values={props.values}
      onClose={props.onClose}
      onClosed={props.onClosed}
    />
  ),
  modalTitle: (mode, row, values) => {
    if (mode === 'create') {
      // department.jsp:56 新增一級部門／:66 新增子部門（樹狀圖的新增子部門會帶 parentId preset）。
      const parent = values === undefined ? undefined : values['parentId'];
      return parent !== undefined && parent !== '' && parent !== '0' ? '新增子部門' : '新增一級部門';
    }
    return String(row?.['parentId'] ?? '0') === '0' ? '修改一級部門' : '修改子部門'; // department.jsp:93,98
  },
  tableArea: (children, ctx, rows) => (
    <DepartmentTableArea table={children} ctx={ctx} texts={DEPARTMENT_TEXTS} rows={rows} />
  ),
};

/** config-role（角色及權限維護，origin role.jsp）。 */
const ROLE_BEHAVIOR: PageBehavior = {
  suppressRowEdit: true, // role.jsp:53-65：操作欄只有刪除；修改掛在角色名稱連結
  editLinkKeys: ['category'], // 角色名稱欄＝.showEdit（role.jsp:53-65）
  deleteVisible: (row) => String(row['deletable'] ?? '') === '1', // role.jsp:32-45 `row.id>=1000`
  toggleVisible: () => false, // legacy 角色頁沒有狀態欄，也沒有切換鈕
  cellOverride: (row, columnKey, ctx) => {
    if (columnKey === 'text2') {
      return <RoleSettingCell row={row} ctx={ctx} />;
    }
    return null;
  },
  modalColumns: (columns) => {
    // 批次 12（B1-6）role.jsp:388-428 modal 欄位：排序序號（required 6，:389）／角色名稱
    // （required 30，:395）／說明（textarea 6 行 280，:401）／系統控制標記 select（:407-416）／
    // 流程控制標記 select（:417-428）。表格欄的 text2（權限設定連結）不是表單欄位，不進 modal。
    const out: PageColumn[] = [];
    for (const column of columns) {
      if (column.key === 'score') {
        out.push({ ...column, label: '排序序號' });
      } else if (column.key === 'category') {
        out.push({ ...column, label: '角色名稱' });
      } else if (column.key === 'text') {
        out.push({ ...column, label: '說明' });
      }
    }
    // 系統控制標記／流程控制標記（role.jsp:407-428）：select 的 JSP 自帶靜態選項「無」
    // （value=''，:410/:421）＋ sysRoleList/flowRoleList（enumConfig DB，RoleController.java:58-59）。
    // 雛形無 DB 內容 → 只有「無」，是 JSP 自帶的靜態選項，非 invent【雛形值待實機校準】。
    out.push({ key: 'sysRole', label: '系統控制標記', kind: 'status', pool: ['無'] });
    out.push({ key: 'flowRole', label: '流程控制標記', kind: 'status', pool: ['無'] });
    return out;
  },
  modalTitle: (mode) => (mode === 'create' ? '新增角色' : '修改角色'), // role.jsp:145,152
};

/**
 * role「權限設定」欄（role.jsp:49-57）：「角色設定」連結 → 功能權限設定 modal
 * （role.jsp ruleSettingModal）。modal 本體在 `RolePermissionModal.tsx`。
 */
function RoleSettingCell({ row, ctx }: { row: Row; ctx: BehaviorFlowContext }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {/* role.jsp:198-207 `.showRoleSetting` 連結；D-3：開對話框的元素自報 data-modal-trigger。 */}
      <Button
        uiId={`${ctx.pageId}-row-${toUiKey(String(row.id ?? ''))}-role-setting`}
        variant="default"
        size="sm"
        type="button"
        dataModalTrigger={`${ctx.pageId}-permission-dialog`}
        onClick={() => {
          setOpen(true);
        }}
      >
        角色設定
      </Button>
      {open ? (
        <RolePermissionModal
          pageId={ctx.pageId}
          row={row}
          onClose={() => {
            setOpen(false);
          }}
          onSaved={() => {
            setOpen(false);
            ctx.reload();
          }}
        />
      ) : null}
    </>
  );
}

/** config-location（地點維護，origin location.jsp）。 */
const LOCATION_BEHAVIOR: PageBehavior = {
  suppressRowEdit: true, // location.jsp 操作欄只有刪除；修改掛在地點名稱連結
  editLinkKeys: ['unit4'], // 地點名稱欄＝.showEdit
  // location.jsp:20 顯示條件（isPermitDelete && row.erasable==1 && row.isEnable==1）
  // 不含 isUsed——已使用的啟用列仍顯示刪除連結，點了才被 usage check alert。
  deleteVisible: (row) => String(row['erasable'] ?? '') === '1' && String(row['status'] ?? '') === '啟用',
  toggleVisible: () => false, // 切換連結畫在狀態欄（cellOverride）
  usageCheck: {
    disableUsed: '地點已被使用，停用後將不可再使用此地點，是否確定要停用?', // location.jsp:261
    disableUnused: '停用後將不可再使用此地點，是否確定要停用?', // location.jsp:259
    enable: '是否確定要啟用?', // location.jsp:264
    deleteUsed: '地點已被使用，不能刪除，請改成停用', // location.jsp:269
  },
  cellOverride: (row, columnKey, ctx) => {
    if (columnKey === 'status') {
      return (
        <StatusToggleCell
          row={row}
          columnKey={columnKey}
          ctx={ctx}
          texts={LOCATION_BEHAVIOR.usageCheck as UsageCheckTexts}
          canToggle={() => true}
        />
      );
    }
    return null;
  },
  importBlock: (ctx) => <ImportToolbar ctx={ctx} spec={importSpecFor('config-location')} />, // 批次 9：匯入地點資料（location.jsp:298-352／:427-442）；批次 32 移 root 層（CFG-001）
};

/** config-account-v-2 的批量勾選與工具列（accountV2.jsp）。 */

/**
 * 勾選欄 checkbox（accountV2.jsp:19-23）：「勾選停用」欄只出現在啟用列、「勾選啟用」欄
 * 只出現在停用列——停用帳號/啟用帳號批量鈕依勾選集合作用。
 */
function AccountCheckCell({
  row,
  ctx,
  bucket,
  visible,
}: {
  row: Row;
  ctx: BehaviorFlowContext;
  /** 勾選桶：帳號維護 disable／enable（accountV2.jsp:19-23）、開課管理 batchDelete（clazzList_js.jsp:85-91）。 */
  bucket: string;
  visible: boolean;
}) {
  const selection = ctx.selection;
  if (!visible || selection === undefined) {
    return null;
  }
  const id = String(row.id ?? '');
  return (
    <input
      type="checkbox"
      data-ui-id={`${ctx.pageId}-row-${toUiKey(id)}-check-${toUiKey(bucket)}`}
      checked={selection.has(bucket, id)}
      onChange={() => {
        selection.toggle(bucket, id);
      }}
    />
  );
}

/**
 * 帳號維護工具列（accountV2.jsp:674-677 停用帳號/啟用帳號/清除被封鎖IP/人事同步；
 * :719-725 匯入帳號頭像 file input＋批次上傳頭像）。
 * 批量停用/啟用真的打 dataSource.update（C5-j）；清除被封鎖IP／人事同步／頭像上傳
 * 是後端專屬操作，雛形以流程呈現、不呼叫 DataSource，所以**不帶 data-effect**。
 */
function AccountBulkToolbar({ ctx }: { ctx: BehaviorFlowContext }) {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const [syncOpen, setSyncOpen] = useState(false);
  const [avatarFiles, setAvatarFiles] = useState<readonly File[]>([]);
  const selection = ctx.selection;

  const bulkFlow = (bucket: 'disable' | 'enable', emptyText: string, confirmText: string) => {
    void (async () => {
      if (selection === undefined) {
        return;
      }
      const ids = selection.ids(bucket);
      if (ids.length === 0) {
        await dialogs.alert(emptyText);
        return;
      }
      const confirmed = await dialogs.confirm(confirmText);
      if (!confirmed) {
        return;
      }
      try {
        // legacy 一次 POST 清單（disableAccountAjax {list}）；雛形逐筆 update（同一個 op）。
        await dialogs.processing.wrap(
          Promise.all(
            ids.map((id) =>
              dataSource.update(ctx.pageId, id, { status: bucket === 'disable' ? '停用' : '啟用' }),
            ),
          ),
        );
        selection.clear();
        ctx.reload();
        await dialogs.info(t('state.saveSuccess'));
      } catch (cause: unknown) {
        await dialogs.alert(legacyAlertFallback(cause));
      }
    })();
  };

  const uploadAvatars = () => {
    void (async () => {
      // 【legacy-known-issue 照實保留】accountV2.jsp:15 的 MIME 白名單只有 image/jpeg 與
      // image/png——accept 屬性雖然允許 .bmp（:718），JS 檢核卻會把 bmp 判為錯誤檔。
      // 雛形鏡像這個行為：.bmp 上傳會進錯誤清單。
      const invalid = avatarFiles.filter(
        (file) => !['.jpeg', '.jpg', '.png'].some((ext) => file.name.toLowerCase().endsWith(ext)),
      );
      if (invalid.length > 0) {
        // accountV2.jsp:117-118 逐字（<p> 在 legacy 是 alert 的 HTML 換段，React 以 <br/> 呈現）。
        await dialogs.alert(
          <span>
            圖片檔案類型錯誤，僅能上傳[png、jpeg、jpg、bmp]類型圖片！
            <br />
            以下為錯誤檔案:
            <br />
            {invalid.map((file) => (
              <span key={file.name} className="block">
                {file.name}
              </span>
            ))}
          </span>,
        );
        return;
      }
      const confirmed = await dialogs.confirm('是否確定要匯入頭像？'); // accountV2.jsp:120 逐字
      if (!confirmed) {
        return;
      }
      await dialogs.processing.wrap(new Promise<void>((resolve) => {
        setTimeout(resolve, 0);
      }));
      // legacy POST config/accountV2/batchUploadAvatar（accountV2.jsp:127-133）；雛形不落地檔案。
      await dialogs.info('頭像上傳完成');
    })();
  };

  return (
    <span data-ui-id={`${ctx.pageId}-account-bulk-actions`} className="inline-flex flex-wrap items-center gap-2">
      <Button
        uiId={`${ctx.pageId}-bulk-disable`}
        variant="danger"
        size="sm"
        type="button"
        dataEffect={`update:${ctx.pageId}`}
        onClick={() => {
          bulkFlow('disable', '沒有勾選停用帳戶!', '是否確定要停用帳戶?'); // accountV2.jsp:235,238 逐字
        }}
      >
        停用帳號
      </Button>
      <Button
        uiId={`${ctx.pageId}-bulk-enable`}
        variant="primary"
        size="sm"
        type="button"
        dataEffect={`update:${ctx.pageId}`}
        onClick={() => {
          bulkFlow('enable', '沒有勾選啟用帳戶!', '是否確定要啟用帳戶?'); // accountV2.jsp:274,277 逐字
        }}
      >
        啟用帳號
      </Button>
      <Button
        uiId={`${ctx.pageId}-bulk-clean-forbid-ip`}
        variant="default"
        size="sm"
        type="button"
        onClick={() => {
          // legacy：GET config/accountV2/cleanAllForbidIp（accountV2.jsp:520-531）；雛形不接後端。
          void (async () => {
            await dialogs.processing.wrap(new Promise<void>((resolve) => {
              setTimeout(resolve, 0);
            }));
            await dialogs.info('已清除被封鎖IP');
          })();
        }}
      >
        清除被封鎖IP
      </Button>
      {/* accountV2.jsp:677 `$('#syncPeopleModal').modal('show')`；D-3 自報。 */}
      <Button
        uiId={`${ctx.pageId}-bulk-sync-people`}
        variant="default"
        size="sm"
        type="button"
        dataModalTrigger={`${ctx.pageId}-sync-people-dialog`}
        onClick={() => {
          setSyncOpen(true);
        }}
      >
        人事同步
      </Button>
      <span className="inline-flex items-center gap-1">
        {/* W4（批次 42，issue #40）：頭像區說明群（accountV2.jsp:718-736）——
            :718 label 匯入帳號頭像：→ :721 file input → :724 批次上傳頭像（legacy 也是
            `<button type=button>`，形態同）→ :730 label 使用說明：→ :734-736 ul li。
            label 與 input 是兄弟（legacy .row 內 col 分欄＝兄弟層），不包 input。 */}
        {/* biome-ignore lint/a11y/noLabelWithoutControl: 逐字鏡像 legacy 純文字 label（:718 col-sm-12，全形冒號）。 */}
        <label className="text-sm font-bold text-ink">匯入帳號頭像：</label>
        <input
          type="file"
          data-ui-id={`${ctx.pageId}-avatar-file`}
          accept=".png, .jpeg, .jpg, .bmp"
          multiple
          className="text-xs"
          onChange={(event) => {
            setAvatarFiles([...(event.target.files ?? [])]);
          }}
        />
        <Button
          uiId={`${ctx.pageId}-bulk-upload-avatars`}
          variant="primary"
          size="sm"
          type="button"
          onClick={uploadAvatars}
        >
          批次上傳頭像
        </Button>
      </span>
      {/* W4：頭像使用說明（:730 label＋:734-736 ul li，逐字）。 */}
      {/* biome-ignore lint/a11y/noLabelWithoutControl: 逐字鏡像 legacy 純文字 label（:730 col-sm-3，全形冒號）。 */}
      <label className="text-sm font-bold text-ink">使用說明：</label>
      <ul className="list-disc pl-5 text-xs text-ink">
        <li>請使用身分證字號來當作檔名。</li> {/* :735 逐字 */}
      </ul>
      {syncOpen ? (
        <SyncPeopleModal
          pageId={ctx.pageId}
          onClose={() => {
            setSyncOpen(false);
          }}
        />
      ) : null}
    </span>
  );
}

/** config-account-v-2（帳號維護，origin accountV2.jsp）。 */
const ACCOUNT_BEHAVIOR: PageBehavior = {
  editLinkKeys: ['person'], // 姓名 .showEdit → 修改 modal（accountV2.jsp:41-48）
  deleteVisible: () => false, // 表格沒有列動作（契約 rowActions: []）；刪除語意在停用帳號
  toggleVisible: () => false, // 狀態是純文字；啟停靠勾選批量鈕
  selectionBuckets: ['disable', 'enable'],
  cellOverride: (row, columnKey, ctx) => {
    if (columnKey === 'text') {
      // 「功能列勾選停用」欄：只出現在啟用列（accountV2.jsp:19-21）。
      return <AccountCheckCell row={row} ctx={ctx} bucket="disable" visible={String(row['status'] ?? '') === '啟用'} />;
    }
    if (columnKey === 'text2') {
      // 「功能列勾選啟用」欄：只出現在停用列（accountV2.jsp:22-23）。
      return <AccountCheckCell row={row} ctx={ctx} bucket="enable" visible={String(row['status'] ?? '') === '停用'} />;
    }
    return null;
  },
  bulkActions: (ctx) => <AccountBulkToolbar ctx={ctx} />,
  // 批次 9：匯入帳號資料（accountV2.jsp:536-568／:690-711；頭像在上方 AccountBulkToolbar，批次 1）；
  // 批次 32 移 root 層（CFG-001）——ImportToolbar 與批量工具分離。
  // W3（批次 42，issue #40）：帳號維護說明（:686-687）渲染在匯入區之前——JSP 順序
  // 查詢 form(:607-677) → :686 label 帳號維護說明: → :687 說明文字 → :690 匯入區。
  // :687 前綴 <br>＋&nbsp;×3 是透明/接合節點，normalize 空格接合後畫純文字即可。
  importBlock: (ctx) => (
    <>
      {/* biome-ignore lint/a11y/noLabelWithoutControl: 逐字鏡像 legacy 純文字 label（:686 col-sm-12，半形冒號）。 */}
      <label className="text-sm font-bold text-ink">帳號維護說明:</label>
      <span className="text-sm text-ink">如忘記設定部門，請在醫院選單選擇[未設定]來查詢。</span>
      <ImportToolbar ctx={ctx} spec={importSpecFor('config-account-v-2')} />
    </>
  ),
  modalTitle: (mode) => (mode === 'create' ? '新增帳號' : '修改帳號'), // accountV2.jsp:170,180
  // 批次 10（B1-2）：帳號編輯大 modal（#dialogModal，accountV2.jsp:811-867）——
  // 44+ 欄位 6 分頁＋照片＋部門職稱/證照/角色設定子流程；通用模板 modal 不再使用。
  editModal: (props) => (
    <AccountV2DialogModal
      pageId={props.pageId}
      mode={props.mode}
      row={props.row}
      onClose={props.onClose}
      onClosed={props.onClosed}
    />
  ),
  editModalTriggerId: 'config-account-v-2-account-dialog', // D-3 自報（帳號大 modal 的 uiId）
};

// ---------------------------------------------------------------------------
// 批次 9：匯入（config 模組 7 頁特例，2026-09-22 控制者裁決；執行文件
// docs/plans/2026-09-22-batch9-import-plan.md，進度檔 §1 偵察筆記／§3 設計決策）。
// ---------------------------------------------------------------------------

/** 匯入流程的每頁規格（文案逐字＋JSP 行號；7 頁差異全表見進度檔 §1.2）。 */
interface ImportSpec {
  /** 匯入區標籤（legacy label 逐字，全形：FF1A）。 */
  readonly label: string;
  /** 無檔時的 alert（逐字；comment 是「請選擇匯入檔案!」，其餘是「未選擇檔案!」，皆半形!）。 */
  readonly noFileAlert: string;
  /** 副檔名 JS 檢查（只有 comment.js:173-176 有；其餘 6 頁只靠 accept 屬性，照 legacy 不檢）。 */
  readonly extCheck?: { readonly alert: string };
  /** 查詢表單 org 欄位 name（organizationId／orgName，各頁不同）；annual 沒有（legacy :257 讀了沒用＝legacy-known-issue 照實不讀）。 */
  readonly orgField?: string;
  /** org 未選（legacy 哨兵 -1 → React ''）時的 alert；未給＝legacy 無此檢查（annual/department）。 */
  readonly orgCheckAlert?: string;
  /** confirm 文字；帶 orgName 組字的頁用函式（department.jsp:394／device.jsp:308／jobTitle.jsp:233／location.jsp:310）。 */
  readonly confirm: string | ((orgName: string) => string);
  /** confirm 確定後的失敗訊息（雛形無真後端恆走失敗路徑，§3-C）；undefined＝legacy 無失敗訊息（accountV2.jsp:574-575 catch 只 console.log、無 .fail）→ 照實不顯示【legacy-known-issue 照實保留】。 */
  readonly failureAlert?: string;
  /** 範本檔名（downloadTemplate 參數逐字）。 */
  readonly templateFile: string;
  /** 使用說明前綴（逐字：5 頁半形 `使用說明:` 003A；accountV2 :704／comment.jsp:118 全形 FF1A）。 */
  readonly notesPrefix: string;
  /** 說明列（逐字）。 */
  readonly notes: readonly string[];
  /** W2（批次 42，issue #40）：使用說明前綴以 `<label>` 畫（accountV2.jsp:704 是
   *  `<label class="col-sm-3 control-label">使用說明：</label>`，與範本下載鈕**兄弟**）。
   *  其餘 7 頁 legacy 是裸 #text（annual.jsp:400「使用說明:&nbsp;</li><input…>」等），
   *  維持 span 攤平——統一 label 化會讓那些頁**新增**層級差，必須按頁差異化。 */
  readonly notesLabel?: boolean;
  /** 檔案欄來源：'contract'＝讀查詢區契約欄位（comment 的 uploadFile 已由 LegacyContractFields 渲染，不加第二個 input）；'toolbar'＝工具列自有無 name input（比照批次 1 頭像先例，不污染欄位契約）。 */
  readonly fileSource: 'contract' | 'toolbar';
  /** 契約檔案欄位 name（fileSource='contract' 時必填）。 */
  readonly contractFileField?: string;
}

/**
 * 批次 9：7 頁匯入規格。文案 codepoint 全表見進度檔 §1.1/§1.2（key 兩條 vs 6 頁 JSP inline
 * 的失敗訊息是兩個不同字串：key=全形！FF01（comment.js:425），JSP inline=半形! 0021）。
 * 匯入是後端行為：**不接 mock DataSource、按鈕不帶 data-effect**（B1-3/B4-3 C5-j 同款誠實標註），
 * 等 HttpDataSource 真後端批次。
 */
const CONFIG_IMPORT_SPECS: Readonly<Record<string, ImportSpec>> = {
  'course-clazz-list': {
    // 批次 39（GNL-005-c，issue #39 修復清單）：clazzList.jsp:152-170 匯入課程區
    // （在 queryForm 內＝importBlockInForm slot）；clazzList_js.jsp:2658-2686
    // uploadClazz：:2660 未選擇檔案!（半形!）／:2664 是否確定要匯入課程?（全形？）／
    // :2670 POST course/clazz/uploadClazzAjax（FormData=queryForm；失敗訊息＝response.desc
    // 無寫死靜態文案→failureAlert 不給，accountV2 前例）；範本 ClassTemplate.xlsx
    // （ClazzController.java:140；raw-dom rendered onclick 同）；:165 說明:（半形:）／
    // :169 請使用匯入範本的格式來做課程匯入。；file input name=uploadFile（:159）但
    // 契約無此欄位→fileSource toolbar（批次 1 頭像先例）。
    label: '匯入課程：',
    noFileAlert: '未選擇檔案!',
    confirm: '是否確定要匯入課程?',
    templateFile: 'ClassTemplate.xlsx',
    notesPrefix: '說明:',
    notes: [
      '請使用匯入範本的格式來做課程匯入。',
    ],
    fileSource: 'toolbar',
  },
  'config-account-v-2': {
    // accountV2.jsp:690 label；:693 檔案欄（legacy 無 name）；:538 未選擇檔案!；:541-543 未選擇醫院!
    // （isEmpty||<1，哨兵 -1/0 同樣擋 → React '' 擋）；:547 confirm（FF1F 全形？）；
    // :557 POST config/accountV2/doImportAccountFileAjax（FormData=importAccountFile+orgId）；
    // :574-575 legacy 無失敗訊息（catch 只 console.log；.done 直接接 .always :577 無 .fail）→ failureAlert 不給；
    // :705 範本；:704 使用說明：（FF1A）；:710-711 說明；:696 權限閘 config/account-UPDATE（雛形無權限模型不重現，批次 1 先例同）。
    label: '匯入帳號資料：',
    noFileAlert: '未選擇檔案!', // accountV2.jsp:538 逐字（半形!）
    orgField: 'organizationId',
    orgCheckAlert: '未選擇醫院!', // accountV2.jsp:543 逐字（半形!）
    confirm: '是否確定要匯入帳號資料？', // accountV2.jsp:547 逐字（全形？）
    templateFile: '帳號資料匯入範本.xlsx', // :705
    notesPrefix: '使用說明：', // :704（全形：）
    notes: [
      '請使用匯入範本的格式來做帳號資料匯入。', // :710
      '資料由第四列開始，帳號若已存在，則不重覆匯入。', // :711
    ],
    notesLabel: true, // W2（批次 42，issue #40）：:704 是 label（與 :705 範本下載鈕兄弟）
    fileSource: 'toolbar',
  },
  'config-annual': {
    // annual.jsp:388 label；:393 檔案欄（無 name）；:254 未選擇檔案!；:257 orgName 讀了沒用＝legacy-known-issue（照實不讀）；
    // :260 confirm（FF1F）；:273 POST /config/importAnnualAjax；:294 匯入失敗!（ajax error callback，半形!）；
    // :400 範本＋使用說明:（003A）；:402-403 說明（:403 li 未閉合）；:385 權限閘誤掛 config/department-UPDATE（copy-paste quirk，雛形不重現）。
    label: '匯入年度資料：',
    noFileAlert: '未選擇檔案!', // annual.jsp:254 逐字（半形!）
    confirm: '是否確定要匯入年度資料？', // annual.jsp:260 逐字（全形？）
    failureAlert: '匯入失敗!', // annual.jsp:294 逐字（半形!）
    templateFile: '年度資料匯入範本.xlsx', // :400
    notesPrefix: '使用說明:', // :400（半形:）
    notes: [
      '資料由第三列開始，代碼或名稱若已存在，則不重覆匯入。', // :402
      'Excel欄位從左到右:年度、排序序號、狀態', // :403 逐字（li 未閉合；尾端空格不計）
    ],
    fileSource: 'toolbar',
  },
  'config-organization': {
    // 批次 32（CFG-001，issue #33；KNOWN-NOT-DONE 第 10 列結案）。
    // organization.jsp:371 label（匯入組織資料：）；:376 檔案欄 id=uploadFile 無 name；
    // :271 未選擇檔案!（isEmpty，半形!）；:277 confirm 是否確定要匯入組織資料？（sysConfirm 全形？）；
    // :290 POST config/organization/importOrganizationAjax（FormData=uploadFile）；
    // :311 匯入失敗!（ajax error callback，半形!）；:384 使用說明＋範本；:386 說明。
    // :272 orgName 讀了沒用（FormData 只有 uploadFile）＝legacy-known-issue 照實不讀（annual.jsp:257 同款）。
    // 權限閘 shiro:hasPermission（:370 前）＝雛形不重現（批次 1 先例同）。
    label: '匯入組織資料：',
    noFileAlert: '未選擇檔案!', // organization.jsp:271 逐字（半形!）
    confirm: '是否確定要匯入組織資料？', // :277 逐字（全形？）
    failureAlert: '匯入失敗!', // :311 逐字（半形!）
    templateFile: '組織資料匯入範本.xlsx', // :384
    notesPrefix: '使用說明:', // :383（半形:）
    notes: [
      '若資料已存在，將更新系統資料。', // :386
    ],
    fileSource: 'toolbar',
  },
  'config-comment': {
    // comment.jsp:104 label；:106-107 檔案欄 name=uploadFile（React 契約欄位已有，legacy-fields.generated.ts:73，讀 queryValues）；
    // comment.js:168 請選擇匯入檔案!；:173-176 副檔名檢查（僅能上傳xlsx檔案！FF01）；:174 `$file.val()` 無效清空（少了參數）＝legacy-known-issue 照實保留（React 亦不清值）；
    // :178 confirm＝legacy.unique.35 確定要匯入嗎?（zh-TW.ts:286；101 條分母 key，全 src 首例 t('legacy.*')，理由見進度檔 §3-B）；
    // :397 POST config/comment/importCommentCode；:405 清錯元素 #importFile（實體 input 是 #uploadFile）＝legacy-known-issue；:425 匯入失敗！＝legacy.unique.12（zh-TW.ts:263；catch 路徑）；
    // comment.jsp:120 範本；:118 說明：（FF1A）；:122 說明；:110 權限閘 config/comment-UPDATE（雛形不重現）。
    label: '匯入常用語：',
    noFileAlert: '請選擇匯入檔案!', // comment.js:168 逐字（半形!）
    extCheck: { alert: '僅能上傳xlsx檔案！' }, // comment.js:175 逐字（全形！）
    confirm: t('legacy.unique.35'),
    failureAlert: t('legacy.unique.12'),
    templateFile: '常用語範本.xlsx', // comment.jsp:120
    notesPrefix: '說明：', // comment.jsp:118（全形：）
    notes: [
      '請使用匯入範本的格式來做常用語匯入。', // :122
    ],
    fileSource: 'contract',
    contractFileField: 'uploadFile',
  },
  'config-department': {
    // department.jsp:614 label；:619 檔案欄（無 name）；:388 未選擇檔案!；:394 confirm 帶 orgName（末碼半形?）；
    // **無 org==-1 檢查**（legacy 預設選項＝「全部」ePortfolio.js:1227 setOrganizationElement → React '' 組字「全部」，與 legacy 預設狀態一致；common.js:1212,:1220 同語意通用 helper）；
    // :399-400 FormData 多帶 organizationId 原值（雛形死路徑，僅註解記錄）；:403 POST /config/importDepartmentAjax；
    // :421 匯入失敗!；:626 範本＋使用說明:（003A）；:628 說明；:611 權限閘 config/department-UPDATE（雛形不重現）。
    label: '匯入部門資料：',
    noFileAlert: '未選擇檔案!', // department.jsp:388 逐字（半形!）
    orgField: 'organizationId',
    confirm: (orgName) => `是否確定要匯入${orgName}的部門?`, // department.jsp:394 組字（orgName=option:selected 文字；未選＝「全部」ePortfolio.js:1227）
    failureAlert: '匯入失敗!', // department.jsp:421 逐字（半形!）
    templateFile: '部門資料匯入範本.xlsx', // :626
    notesPrefix: '使用說明:', // :626（半形:）
    notes: [
      '若資料已存在，將更新系統資料。', // :628
    ],
    fileSource: 'toolbar',
  },
  'config-device': {
    // device.jsp:425 label；:430 檔案欄（無 name）；:298 未選擇檔案!；:301-302 org==-1→請選擇醫院；:308 confirm 帶 orgName；
    // :322 POST /config/device/importDeviceAjax；:343 匯入失敗!；:437 範本＋使用說明:（003A）；:439-440 說明；:422 權限閘 config/device-UPDATE（雛形不重現）。
    label: '匯入設備資料：',
    noFileAlert: '未選擇檔案!', // device.jsp:298 逐字（半形!）
    orgField: 'orgName',
    orgCheckAlert: '請選擇醫院', // device.jsp:302 逐字（無驚嘆號）
    confirm: (orgName) => `是否確定要匯入${orgName}的設備?`, // device.jsp:308 組字
    failureAlert: '匯入失敗!', // device.jsp:343 逐字（半形!）
    templateFile: '設備資料匯入範本.xlsx', // :437
    notesPrefix: '使用說明:', // :437（半形:）
    notes: [
      '資料由第三列開始，代碼或名稱若已存在，則不重覆匯入。', // :439
      'Excel欄位從左到右:管轄部門、類別、代碼、名稱、排序序號', // :440 逐字（尾端空格不計）
    ],
    fileSource: 'toolbar',
  },
  'config-job-title': {
    // jobTitle.jsp:327 label；:332 檔案欄（無 name）；:223 未選擇檔案!；:226-227 org==-1→請選擇醫院；:233 confirm 帶 orgName；
    // :246 POST /config/importJobTitleAjax；:267 匯入失敗!；:339 範本＋使用說明:（003A）；:341 說明；:324 權限閘 config/jobTitle-UPDATE（雛形不重現）。
    label: '匯入職稱資料：',
    noFileAlert: '未選擇檔案!', // jobTitle.jsp:223 逐字（半形!）
    orgField: 'organizationId',
    orgCheckAlert: '請選擇醫院', // jobTitle.jsp:227 逐字（無驚嘆號）
    confirm: (orgName) => `是否確定要匯入${orgName}的職稱資料?`, // jobTitle.jsp:233 組字
    failureAlert: '匯入失敗!', // jobTitle.jsp:267 逐字（半形!）
    templateFile: '職稱資料匯入範本.xlsx', // :339
    notesPrefix: '使用說明:', // :339（半形:）
    notes: [
      '若資料已存在，將更新系統資料。', // :341
    ],
    fileSource: 'toolbar',
  },
  'config-location': {
    // location.jsp:427 label；:432 檔案欄（無 name）；:300 未選擇檔案!；:303-304 org==-1→請選擇醫院；:310 confirm 帶 orgName；
    // :324 POST /config/location/importLocationAjax；:345 匯入失敗!；:439 範本＋使用說明:（003A）；:441-442 說明；:424 權限閘 config/location-UPDATE（雛形不重現）。
    label: '匯入地點資料：',
    noFileAlert: '未選擇檔案!', // location.jsp:300 逐字（半形!）
    orgField: 'orgName',
    orgCheckAlert: '請選擇醫院', // location.jsp:304 逐字（無驚嘆號）
    confirm: (orgName) => `是否確定要匯入${orgName}的地點?`, // location.jsp:310 組字
    failureAlert: '匯入失敗!', // location.jsp:345 逐字（半形!）
    templateFile: '地點資料匯入範本.xlsx', // :439
    notesPrefix: '使用說明:', // :439（半形:）
    notes: [
      '資料由第三列開始，代碼或名稱若已存在，則不重覆匯入。', // :441
      'Excel欄位從左到右:管轄部門、類別、代碼、名稱、排序序號', // :442 逐字（尾端空格不計）
    ],
    fileSource: 'toolbar',
  },
};

/** 批次 9：取得該頁的匯入規格（7 頁都登錄；漏登錄是程式錯誤，fail-fast）。 */
function importSpecFor(pageId: string): ImportSpec {
  const spec = CONFIG_IMPORT_SPECS[pageId];
  if (spec === undefined) {
    throw new Error(`ImportSpec 未登錄：${pageId}`);
  }
  return spec;
}

/**
 * 查詢表單 org 選單目前選項的文字（confirm 組字用）。
 * 未選（React ''）＝legacy 哨兵 -1，其預設選項文字是「全部」（ePortfolio.js:1227：
 * setOrganizationElement(isAddFindAll=true) 預插 `<option value="-1">全部</option>` 且瀏覽器預選第一項；
 * common.js:1212,:1220 的通用下拉 helper 有同語意的 new Option("全部", -1)）。
 * 已選：以 optionsFor 反查選項文字（dynamic 選項 value===text，legacy-fields.ts:189-193，仍以 text 為準）。
 */
function orgNameOf(
  pageId: string,
  orgField: string,
  queryValues: Readonly<Record<string, string>> | undefined,
): string {
  const value = queryValues?.[orgField] ?? '';
  if (value === '') {
    return '全部'; // legacy 哨兵 -1 的預設選項文字（ePortfolio.js:1227；common.js:1212,:1220 同語意）
  }
  const field = (legacyContractFieldsFor(pageId) ?? []).find(
    (candidate) => candidate.name === orgField,
  );
  const option =
    field === undefined
      ? undefined
      : optionsFor(pageId, field).find((candidate) => candidate.value === value);
  return option?.text ?? value;
}

/**
 * 範本下載（common.js:1126-1130 downloadTemplate → `$.fileDownload(downloadFileUrl + '/template?fileName=' + …)`）。
 * 雛形無真後端下載管線：以 Blob 觸發同名下載，內容為佔位說明【雛形值待實機校準】；
 * legacy 的下載失敗 alert（:1129 `下載檔案失敗！`）在 Blob 模式不會發生，不實作。
 */
export function downloadTemplateBlob(fileName: string): void {
  // jsdom（單元測試環境）沒有 URL.createObjectURL → 安全跳過；實體瀏覽器才會真出下載。
  // 下載的實際行為屬實機驗證範圍【雛形值待實機校準】。
  if (typeof URL.createObjectURL !== 'function') {
    return;
  }
  const blob = new Blob(
    ['React 雛形佔位範本（legacy 來源：downloadFileUrl/template?fileName=…，實機校準後由真後端提供）'],
    { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
  );
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * 匯入工具列（批次 9，7 頁共用；掛在 CrudListPage 的 bulkActions 掛點）。
 * 流程鏡像各頁 legacy doImport／#doImport handler（行號見 CONFIG_IMPORT_SPECS 註解）：
 * 無檔/副檔名/org 檢查 → sysConfirm → （雛形無真後端，上傳必然失敗）→ 失敗訊息。
 * 不虛構 processing spinner 期間（無真實上傳工作可包；與批次 1 頭像先例的差異見進度檔 §3-C）。
 */
function ImportToolbar({ ctx, spec }: { ctx: BehaviorFlowContext; spec: ImportSpec }) {
  const dialogs = ctx.dialogs;
  const [file, setFile] = useState<File | null>(null);
  const pageId = ctx.pageId;

  const runImport = () => {
    void (async () => {
      // ① 檔案檢查（行號見 spec 註解）。
      if (spec.fileSource === 'contract') {
        // comment：讀查詢區契約欄位 uploadFile 的檔名（onChange 寫進 draftFieldValues）。
        // legacy comment.js:174 `$file.val()` 是無效清空（少了參數）＝legacy-known-issue 照實保留：React 也不清值。
        const name = ctx.queryValues?.[spec.contractFileField ?? ''] ?? '';
        if (name === '') {
          await dialogs.alert(spec.noFileAlert);
          return;
        }
        if (spec.extCheck !== undefined && name.replace(/^.*\./, '') !== 'xlsx') {
          await dialogs.alert(spec.extCheck.alert);
          return;
        }
      } else if (file === null) {
        await dialogs.alert(spec.noFileAlert);
        return;
      }
      // ② org 檢查（legacy val()==-1 → React ''）。
      const orgName =
        spec.orgField === undefined ? '' : orgNameOf(pageId, spec.orgField, ctx.queryValues);
      if (spec.orgCheckAlert !== undefined && spec.orgField !== undefined) {
        const orgValue = ctx.queryValues?.[spec.orgField] ?? '';
        if (orgValue === '') {
          await dialogs.alert(spec.orgCheckAlert);
          return;
        }
      }
      // ③ sysConfirm（逐字；department/device/jobTitle/location 帶 orgName 組字）。
      const confirmed = await dialogs.confirm(
        typeof spec.confirm === 'function' ? spec.confirm(orgName) : spec.confirm,
      );
      if (!confirmed) {
        return;
      }
      // ④ 雛形無真後端：上傳必然失敗，走 legacy 失敗路徑（執行文件 §2.4 決策，進度檔 §3-C）。
      //    legacy 的 FormData 組合（file＋organizationId/orgId）與成功路徑（showInfoDialog(desc)）
      //    都是死路徑，以註解記錄而不寫假組裝程式。
      //    failureAlert===undefined＝legacy 本來就沒有失敗訊息（accountV2.jsp:574-575）→ 照實不顯示。
      if (spec.failureAlert !== undefined) {
        await dialogs.alert(spec.failureAlert);
      }
    })();
  };

  return (
    <span
      data-ui-id={`${pageId}-import-actions`}
      className="inline-flex flex-wrap items-center gap-2"
    >
      {/* W1（批次 42，issue #40）：匯入區標題 label 化——7 頁 legacy 都是
          `<label class="control-label …">匯入○○資料：</label>`（accountV2.jsp:690／
          annual.jsp:388／department.jsp:614／organization.jsp:371／jobTitle.jsp:327／
          device.jsp:425／location.jsp:427）；normalize 剝 class 後＝裸 label，
          class 保留既有視覺。 */}
      {/* biome-ignore lint/a11y/noLabelWithoutControl: 逐字鏡像 legacy 的純文字 label（同 LegacyContractFields.tsx 前例）；htmlFor/id 在 normalize-dom 屬性 allowlist 之外，加了不影響 structure 比對。 */}
      <label className="text-sm font-bold text-ink">{spec.label}</label>
      {spec.fileSource === 'toolbar' ? (
        <input
          type="file"
          accept=".xlsx"
          data-ui-id={`${pageId}-import-file`}
          className="text-xs"
          onChange={(event) => {
            setFile(event.target.files?.[0] ?? null);
          }}
        />
      ) : null}
      <Button uiId={`${pageId}-import-submit`} variant="primary" size="sm" type="button" onClick={runImport}>
        檔案匯入
      </Button>
      {spec.notesLabel === true ? (
        // W2（批次 42，issue #40）：帳號頁「使用說明：」是 label 且與範本下載鈕**兄弟**
        // （accountV2.jsp:704/:705）——label 不包 button，避免 label>button 巢狀層級差。
        // biome-ignore lint/a11y/noLabelWithoutControl: 逐字鏡像 legacy 純文字 label（同上 W1）。
        <label className="text-sm font-bold text-ink">{spec.notesPrefix}</label>
      ) : (
        // 其餘 7 頁 legacy 是裸 #text＋input 兄弟（annual.jsp:400 等）：span 攤平透明，維持原狀。
        <span className="inline-flex items-center gap-1 text-sm text-ink">
          {spec.notesPrefix}
          <Button
            uiId={`${pageId}-import-template`}
            variant="default"
            size="sm"
            type="button"
            onClick={() => {
              downloadTemplateBlob(spec.templateFile);
            }}
          >
            範本下載
          </Button>
        </span>
      )}
      {spec.notesLabel === true ? (
        <Button
          uiId={`${pageId}-import-template`}
          variant="default"
          size="sm"
          type="button"
          onClick={() => {
            downloadTemplateBlob(spec.templateFile);
          }}
        >
          範本下載
        </Button>
      ) : null}
      <ul className="list-disc pl-5 text-xs text-ink">
        {spec.notes.map((note) => (
          <li key={note}>{note}</li>
        ))}
      </ul>
    </span>
  );
}

/** 設備類別碼映射（device.jsp:36-38 constant.deviceType；【雛形值待實機校準】）。 */
const DEVICE_TYPE_LABELS: Readonly<Record<string, string>> = {
  0: '共用',
  1: '模具管理',
  2: '設備管理',
  3: '其他',
};

/** config-comment（常用語維護，origin comment.jsp＋comment.js）。批次 26（#21）補齊其餘行為（匯入＝批次 9 既有）。
 *  三級級聯（org→jobType→dept，comment.js:15-42）＝選項池平列（D26-2）；本人/sysAdmin 權限＝deletable 旗標（D26-1）。 */
const COMMENT_BEHAVIOR: PageBehavior = {
  // 操作欄只有刪除（comment.js:83-90）；修改掛在常用語標題連結（showEdit，comment.js:104-105）。
  suppressRowEdit: true,
  editLinkKeys: ['title'],
  // 批次 44（#42）：查詢 form 尾（comment.jsp:86-92）——常用語：label＋selePrivate checkbox
  // ＋(只顯示自已的常用語內容)。「自已」照 JSP 原文（常見錯字，照實不修）。
  queryTailFields: (ctx, onChange) => (
    <>
      <label className="mb-[5px] inline-block font-bold text-ink">常用語：</label>
      <input
        type="checkbox"
        id="selePrivate"
        name="selePrivate"
        data-ui-id={`${ctx.pageId}-search-sele-private`}
        checked={(ctx.queryValues?.['selePrivate'] ?? '') === 'on'}
        onChange={(event) => {
          onChange('selePrivate', event.target.checked ? 'on' : '');
        }}
        className="size-4 accent-brand"
      />
      {/* biome-ignore lint/a11y/noLabelWithoutControl: 逐字鏡像 legacy 純文字 label。 */}
      <label htmlFor="selePrivate" className="text-sm text-ink">
        (只顯示自已的常用語內容)
      </label>
    </>
  ),
  // 刪除/啟停條件（comment.js:85-87/:122-133）：本人或 sysAdmin → deletable 旗標【雛形值待實機校準】。
  deleteVisible: (row) => isCommentOwnedRow(row),
  toggleVisible: () => false, // 切換連結畫在狀態欄（cellOverride）
  usageCheck: {
    // comment 無 usage check（checkEnableUsage 直接 confirm，comment.js:356-369）——雙分支同文案；
    // deleteUsed 欄位為介面必填（種子 usageUsed='0' 不觸發）。
    deleteUsed: '刪除後無法復原，確定要刪除嗎?',
    disableUsed: '是否確定要停用?', // comment.js:358
    disableUnused: '是否確定要停用?', // comment.js:358
    enable: '是否確定要啟用?', // comment.js:364
    deleteConfirm: '刪除後無法復原，確定要刪除嗎?', // comment.js:337
  },
  cellOverride: (row, columnKey, ctx) => {
    if (columnKey === 'status') {
      return (
        <StatusToggleCell
          row={row}
          columnKey={columnKey}
          ctx={ctx}
          texts={COMMENT_BEHAVIOR.usageCheck as UsageCheckTexts}
          canToggle={(target) => isCommentOwnedRow(target)}
        />
      );
    }
    return null;
  },
  // modal 欄（comment.jsp:155-248 dialogModal 10 欄）：去狀態欄；roleLimit（selectpicker 多選）
  // 與 useType==451 聯動不鏡像＝D26-3；jobTypeId==0 myJobTypeId trick 不鏡像＝D26-3。
  modalColumns: (columns) => columns.filter((column) => column.key !== 'status'),
  modalTitle: (mode) => (mode === 'create' ? '新增常用語' : '修改常用語'), // comment.js:189,210
  // 非本人且非 sysAdmin→隱藏存檔鈕（comment.js:255-258）＝唯讀 modal（deletable 旗標等價）。
  modalReadOnly: (row) => !isCommentOwnedRow(row),
  importBlock: (ctx) => <ImportToolbar ctx={ctx} spec={importSpecFor('config-comment')} />, // 批次 32 移 root 層（CFG-001）
};

/** config-device（設備維護，origin device.jsp 560 行）。批次 26（#21）補齊（匯入＝批次 9 既有）。
 *  與 jobTitle.jsp 同構（G1：軟刪/啟停三段式 checkUsage）；照片欄/上傳不鏡像＝D26-3。 */
const DEVICE_BEHAVIOR: PageBehavior = {
  // 操作欄只有刪除（device.jsp:18-24）；修改掛在設備名稱連結（showEdit，:42-43）。
  suppressRowEdit: true,
  editLinkKeys: ['title'],
  // 刪除條件（:20）：isPermitDelete && erasable==1 && isEnable==1。
  deleteVisible: (row) => String(row['erasable'] ?? '') === '1' && String(row['status'] ?? '') === '啟用',
  toggleVisible: () => false, // 切換連結畫在狀態欄（cellOverride）
  usageCheck: {
    deleteUsed: '設備已被使用，不能刪除，請改成停用', // device.jsp:264
    disableUsed: '設備已被使用，停用後將不可再使用此設備，是否確定要停用?', // :256
    disableUnused: '停用後將不可再使用此設備，是否確定要停用?', // :254
    enable: '是否確定要啟用?', // :259
  },
  cellOverride: (row, columnKey, ctx) => {
    if (columnKey === 'status') {
      return (
        <StatusToggleCell
          row={row}
          columnKey={columnKey}
          ctx={ctx}
          texts={DEVICE_BEHAVIOR.usageCheck as UsageCheckTexts}
          canToggle={(target) => String(target['erasable'] ?? '') === '1'}
        />
      );
    }
    if (columnKey === 'unit2') {
      // 管轄部門（:29-34）：departmentId '0'→'共用'，其餘部門名。
      const value = String(row['unit2'] ?? '');
      return value === '0' ? <span>共用</span> : null;
    }
    if (columnKey === 'category') {
      // 設備類別（:36-38）：constant.deviceType 碼映射（0 共用/1 模具管理/2 設備管理/3 其他）。
      const code = String(row['category'] ?? '');
      const label = DEVICE_TYPE_LABELS[code];
      return label !== undefined ? <span>{label}</span> : null;
    }
    return null;
  },
  // modal 欄（device.jsp:473-556 dialogModal 7 欄）：去照片（text）與狀態（isEnable 不在 modal）；
  // code 可編輯（:513）＝React id 欄唯讀（模板語意）＝D26-4；照片上傳不鏡像＝D26-3。
  modalColumns: (columns) => columns.filter((column) => column.key !== 'text' && column.key !== 'status'),
  modalTitle: (mode) => (mode === 'create' ? '新增設備' : '修改設備'), // device.jsp:102,124
  // isEnable==0→全 input disabled＋saveBtn 隱藏（:109-112）。
  modalReadOnly: (row) => String(row['status'] ?? '') === '停用',
  importBlock: (ctx) => <ImportToolbar ctx={ctx} spec={importSpecFor('config-device')} />, // 批次 32 移 root 層（CFG-001）
};

/** config-job-title（職稱維護，origin jobTitle.jsp 417 行）。批次 26（#21）補齊（匯入＝批次 9 既有）。
 *  與 device.jsp 同構（G1）；check 端點錯字 checkJobTitlAjax（:176）＝legacy-known-issue 照實（mock 不消費）。 */
/**
 * crud-fix（2026-10-04，P36 反轉）：modal 醫院下拉的選項池——getMyOrganizationListAjax
 * 的 mock 版＝ACCOUNT_ORGANIZATION_OPTIONS 同源（帳號頁醫院下拉同池）；label 即值
 * （validateResourceWrite 只驗非空「請選擇醫院」，jobTitle.jsp:142）。
 */
const JOB_TITLE_ORG_OPTIONS: readonly string[] = ACCOUNT_ORGANIZATION_OPTIONS.map((option) => option.label);

const JOB_TITLE_BEHAVIOR: PageBehavior = {
  suppressRowEdit: true,
  editLinkKeys: ['text'], // 職稱欄＝.showEdit（jobTitle.jsp:32-33）
  // 刪除條件（:20）：isPermitDelete && erasable==1 && isEnable==1。
  deleteVisible: (row) => String(row['erasable'] ?? '') === '1' && String(row['status'] ?? '') === '啟用',
  toggleVisible: () => false,
  usageCheck: {
    deleteUsed: '職稱已被使用，不能刪除，請改成停用', // jobTitle.jsp:192
    disableUsed: '職稱已被使用，停用後將不可再使用此職稱，是否確定要停用?', // :184
    disableUnused: '停用後將不可再使用此職稱，是否確定要停用?', // :182
    enable: '是否確定要啟用?', // :187
  },
  cellOverride: (row, columnKey, ctx) => {
    if (columnKey === 'status') {
      return (
        <StatusToggleCell
          row={row}
          columnKey={columnKey}
          ctx={ctx}
          texts={JOB_TITLE_BEHAVIOR.usageCheck as UsageCheckTexts}
          canToggle={(target) => String(target['erasable'] ?? '') === '1'}
        />
      );
    }
    return null;
  },
  // modal 欄（jobTitle.jsp:369-417 dialogModal）：organizationId/code/name/sort——表欄缺醫院
  // ＝補 pseudo 欄；去狀態（isEnable 不在 modal）。
  // crud-fix（P36 反轉）：org 欄由 text input 改 select（legacy setOrganizationElement 醫院
  // 下拉；選項＝JOB_TITLE_ORG_OPTIONS）——kind:'status'＋pool＝FieldControl 畫 Select 的
  // 既有通道（表格欄不進列表渲染、toggleActionOf 只認表格欄，無副作用）。
  modalColumns: (columns) => [
    { key: 'organizationId', label: '醫院', kind: 'status', pool: JOB_TITLE_ORG_OPTIONS },
    ...columns.filter((column) => column.key !== 'status'),
  ],
  modalTitle: (mode) => (mode === 'create' ? '新增職稱' : '修改職稱'), // jobTitle.jsp:76,86
  // isEnable==0→全 input disabled＋saveBtn 隱藏（:87-91）。
  modalReadOnly: (row) => String(row['status'] ?? '') === '停用',
  // crud-fix（P35 反轉）：職稱代碼欄新增/修改皆可填（jobTitle.jsp showModal :12/:23
  // `$('#code').val(...)`；save() :126-131 code required）；create() 對帶值 id 尊重。
  modalIdEditable: () => true,
  // crud-fix（P36 鏡像）：修改 modal 醫院欄回填後 disabled（showModal
  // `$("#dialogForm #organizationId").attr("disabled", true)`）。
  modalFieldDisabled: (columnKey, mode) => columnKey === 'organizationId' && mode === 'edit',
  importBlock: (ctx) => <ImportToolbar ctx={ctx} spec={importSpecFor('config-job-title')} />, // 批次 32 移 root 層（CFG-001）
};

/** config-organization（組織維護，origin organization.jsp 493 行）。批次 26（#21）補齊。
 *  G2（checkUsage 變體）；匯入＝批次 32（CFG-001，issue #33）：spec 條目＋importBlock（root 層）——
 *  KNOWN-NOT-DONE 第 10 列結案；
 *  查詢 onchange 即時查（:348/:358 無送出鈕）＝用查詢鈕等價（D26-2）；本機帳號 T6＝
 *  showEdit 名稱連結文字（labelIsRowData，editLinkKeys 涵蓋）。 */
const ORGANIZATION_BEHAVIOR: PageBehavior = {
  suppressRowEdit: true,
  editLinkKeys: ['unit2'], // 組織名稱欄＝.showEdit（organization.jsp:35-36；raw-dom 本機帳號＝此連結文字）
  // 刪除條件（:19）：isPermitDelete && row.erasable==1 && row.enable==1。
  deleteVisible: (row) => String(row['erasable'] ?? '') === '1' && String(row['status'] ?? '') === '啟用',
  toggleVisible: () => false,
  usageCheck: {
    deleteUsed: '組織已被使用，不能刪除，請改成停用', // organization.jsp:259
    disableUsed: '組織已被使用，停用後將不可再使用此組織，是否確定要停用?', // :227
    disableUnused: '停用後將不可再使用此組織，是否確定要停用?', // :233
    enable: '是否確定要啟用?', // :244
  },
  // 批次 32（CFG-001，issue #33）：匯入組織資料（organization.jsp:265-321／:375-386）——root 層。
  importBlock: (ctx) => <ImportToolbar ctx={ctx} spec={importSpecFor('config-organization')} />,
  // W11（批次 42，issue #40，裁決④案 B）：組織類型欄（label「組織類型：」:346＋select
  // queryType :348-353 全部""/主要組織 main/外訓組織 community，行號親驗）——queryType 無
  // name＝observed 抽不到、契約無此欄（契約凍結面零觸碰），以行為層 extraFields 補畫；
  // 受控值存草稿 values.type（legacy doQuery 送 type 參數，:124）；onchange="doQuery();"
  // ＝選了立刻查（由 CrudListPage 的 onChange 接線承擔）。
  queryExtraFields: (ctx, onChange) => (
    <Fragment key="config-organization-querytype">
      {/* biome-ignore lint/a11y/noLabelWithoutControl: 逐字鏡像 legacy 的純文字 label（LegacyContractFields 同款）；htmlFor/id 在 normalize-dom 屬性 allowlist 之外。 */}
      <label style={FULL_ROW_STYLE} className={FIELD_LABEL_CLASS}>
        組織類型：
      </label>
      <select
        style={FULL_ROW_STYLE}
        data-ui-id={`${ctx.pageId}-search-querytype`}
        className={FIELD_BASE_CLASS}
        value={ctx.queryValues?.['type'] ?? ''}
        onChange={(event) => {
          onChange('type', event.target.value);
        }}
      >
        <option value="">全部</option>
        <option value="main">主要組織</option>
        <option value="community">外訓組織</option>
      </select>
    </Fragment>
  ),
  cellOverride: (row, columnKey, ctx) => {
    if (columnKey === 'status') {
      return (
        <StatusToggleCell
          row={row}
          columnKey={columnKey}
          ctx={ctx}
          texts={ORGANIZATION_BEHAVIOR.usageCheck as UsageCheckTexts}
          canToggle={(target) => String(target['erasable'] ?? '') === '1'}
        />
      );
    }
    if (columnKey === 'unit') {
      // 組織類型（:25-30）：main→主要組織/community→外訓組織。
      const type = String(row['unit'] ?? '');
      if (type === 'main') return <span>主要組織</span>;
      if (type === 'community') return <span>外訓組織</span>;
      return null;
    }
    return null;
  },
  // modal 欄（organization.jsp:418-493 dialogModal）：type/code/name/shortName/sort；
  // 去狀態（isEnable 隱藏 :473）；isAdmin 權限欄隱藏（:462）且 save 寫死 0（:154）＝D26-6。
  modalColumns: (columns) => columns.filter((column) => column.key !== 'status'),
  modalTitle: (mode) => (mode === 'create' ? '新增組織名稱' : '修改組織名稱'), // organization.jsp:100,113
  // enable==0→btnSave 隱藏＋name/shortName/code/type/sort 全 disabled（:78-84）。
  modalReadOnly: (row) => String(row['status'] ?? '') === '停用',
};

/** config-comment 本人列判斷（comment.js:85-87 accountId==loginAccountId || isSysAdmin）。
 *  種子明確帶 deletable '0'/'1'；UI 新增列（本人建立）沒有此旗標 → 視為本人可刪/可停用（issue #52）。 */
export function isCommentOwnedRow(row: Record<string, unknown>): boolean {
  const flag = row['deletable'];
  return flag === undefined || flag === null || String(flag) === '1';
}

/** config-forbid-word 刪除門檻（forbidWord.jsp:17 id>99）。UI 新增列 id 為非數字（如 CON-114-9001）→ 視為可刪（issue #52）。 */
export function isForbidWordDeletable(row: Record<string, unknown>): boolean {
  const id = Number(row['id'] ?? 0);
  return Number.isNaN(id) ? true : id > 99;
}

/** config-forbid-word（禁用語維護，origin forbidWord.jsp 208 行）。批次 26（#21）補齊。
 *  G3 極簡：無查詢表單（ready 即查 :39）、無啟停；刪除門檻 row.id>99（:17，前 100 筆系統種子不可刪）；
 *  權限 shiro 無包裹（isPermitUpdate 寫死 true :10）＝全欄可用。 */
const FORBID_WORD_BEHAVIOR: PageBehavior = {
  suppressRowEdit: true,
  editLinkKeys: ['title'], // 禁用語欄＝.showEdit（isPermitUpdate，forbidWord.jsp:25）
  // 刪除條件（:17）：isPermitUpdate && row.id > 99（種子 id '50'＝不可刪邊界）。
  deleteVisible: (row) => isForbidWordDeletable(row),
  toggleVisible: () => false, // 無啟停
  modalTitle: (mode) => (mode === 'create' ? '新增禁用語' : '修改禁用語'), // forbidWord.jsp:49,55
  // 無 usage check（deleteFlow 直接 confirm；:119 逐字——模板 legacy message resolver 會
  // 解到 global.02 是否確定要刪除檔案?＝錯字串，行為層覆寫）。
  deleteConfirm: '是否確定要刪除?',
};

/** config-job-type（訓練類別設定，origin jobType.jsp 577 行，display 頁型）。批次 26（#21）。
 *  G2；表欄 title 由 JS columns 提供（:28-76，表無 thead :463）＝complexBody 整頁置換。 */
const JOB_TYPE_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <JobTypeBody pageId={pageId} />,
  complexBodySubmitId: 'config-job-type-query-btn', // doQuery jobType.jsp:224 → config/getJobTypeListAjax
};

/** config-job-type-department（訓練類別部門維護，origin jobTypeDepartment.jsp 512 行）。批次 26（#21）。
 *  G5 雙欄穿梭 picklist；新增/刪除＝legacy dead code 照實不畫（D26-7）＝complexBody 整頁置換。 */
const JOB_TYPE_DEPARTMENT_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <JobTypeDepartmentBody pageId={pageId} />,
  complexBodySubmitId: 'config-job-type-department-query-btn', // doQuery :157-165 → config/getJobTypeByOrgIdAjax
};

/** pageId → 行為。未登錄的頁回 undefined，模板走原本的通用路徑。 */
/** data-ui-id 用的 kebab 化（同 shared.toUiKey 規則；此檔不 import shared 以免循環）。 */
function toKebab(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[^A-Za-z0-9]+/g, '-')
    .toLowerCase()
    .replace(/^-+|-+$/g, '');
}

/**
 * 複製列動作（clazzList_js.jsp:80-84 copyClass → copyClassAjax {classId} → 重查＋開新課程 modal）。
 * 雛形：dataSource.create(整列複本)（新編號由 create 產生）→ reload → 開修改 modal。
 */
function CopyClassAction({ row, ctx }: { row: Row; ctx: BehaviorFlowContext }) {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const [busy, setBusy] = useState(false);
  return (
    <Button
      uiId={`${ctx.pageId}-row-${toUiKey(String(row.id ?? ''))}-copy`}
      variant="default"
      size="sm"
      type="button"
      disabled={busy}
      // 語意＝建立一筆複本，走 create()（D-1 五個 op 之內）。
      dataEffect={`create:${ctx.pageId}`}
      onClick={() => {
        void (async () => {
          setBusy(true);
          try {
            const { id: _omit, ...cloneSource } = row;
            const created = await dialogs.processing.wrap(dataSource.create(ctx.pageId, cloneSource));
            ctx.reload();
            // legacy 複製成功有 showInfoDialog(desc)（clazzList_js.jsp:2321-2325；複驗 F4）。
            await dialogs.info(t('state.saveSuccess'));
            ctx.openEdit(created);
          } catch (cause: unknown) {
            await dialogs.alert(legacyAlertFallback(cause));
          } finally {
            setBusy(false);
          }
        })();
      }}
    >
      複製
    </Button>
  );
}

/** 批次 22（B2-2）：事件色（clazzList_js.jsp:11-12 classColor/meetingColor；clazzQueryList.js:5-6 同）。 */
const COURSE_CALENDAR_CLASS_COLOR = '#E5B12E';
const COURSE_CALENDAR_MEETING_COLOR = '#348DF6';

/** `YYYY-MM-DD`／`YYYY/MM/DD` → { year, month }（溢位日／超範月照 moment 解析行為 rollover：
 *  9/31→10/1；空值或非日期＝null＝moment Invalid 對應）。 */
function parseCourseCalendarDate(value: string): { year: number; month: number } | null {
  const normalized = value.trim().replace(/\//g, '-');
  const match = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(normalized);
  if (match === null) return null;
  const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return { year: parsed.getFullYear(), month: parsed.getMonth() };
}

/**
 * 課程行事曆的跨月檢查（ClassUtils.calendarQueryCheck，classUtils.js:319-329）:
 * 兩個日期同月才可檢視；不同月/無效日期 → showAlertDialog「行事曆檢視日期不能跨月份!"
 * （moment Invalid 的 isSame 恆 false，空值同樣不通過）。
 */
function courseCalendarQueryCheck(
  beginDate: string,
  endDate: string,
  dialogs: BehaviorFlowContext['dialogs'],
): boolean {
  const begin = parseCourseCalendarDate(beginDate);
  const end = parseCourseCalendarDate(endDate);
  const sameMonth =
    begin !== null && end !== null && begin.year === end.year && begin.month === end.month;
  if (!sameMonth) {
    void dialogs.alert('行事曆檢視日期不能跨月份!');
  }
  return sameMonth;
}

/**
 * 開課管理／課程查詢的行事曆／列表切換（clazzList.jsp:139-141 #display-calendar）。
 * 批次 22（B2-2，Gitea issue #7）：改以 LightCalendar 月曆鏡像 fullCalendar month view——
 * 跨月檢查（classUtils.js:319-329）＋事件（title=course.name、start/end=classBegin/EndDateTimeString、
 * 色 kind class→#E5B12E/meeting→#348DF6，clazzList_js.jsp:280-296／clazzQueryList.js:1201-1235）＋
 * viewRender 月起訖寫回查詢表單並重查（clazzList_js.jsp:2862-2868）。agendaWeek/agendaDay
 * 不鏡像（D19-x/D20-6 延續裁定：自製月曆只有 month 檢視）。點活動＝showClass（開修改 modal；
 * 課程查詢＝calendarItemRenderer→CourseViewModal，clazzQueryList.js:1176-1178）。
 */
function CourseCalendarToggle({
  table,
  ctx,
  rows,
}: {
  table: ReactElement;
  ctx: BehaviorFlowContext;
  rows: readonly Row[];
}) {
  const [mode, setMode] = useState<'table' | 'calendar'>('table');
  const queryValues = ctx.queryValues ?? {};
  const beginDate = String(queryValues['beginDate'] ?? '');
  const endDate = String(queryValues['endDate'] ?? '');

  // legacy #btnQuery 把 #display 設回 table（clazzList_js.jsp:165-168）——行事曆檢視中按
  // 查詢鈕＝切回列表（searchVersion 由 CrudListPage.applySearch 遞增；我們自己的
  // applyQueryValues 寫回不遞增，不會誤觸切回）。
  const searchVersion = ctx.searchVersion ?? 0;
  const seenSearchVersion = useRef(searchVersion);
  useEffect(() => {
    if (searchVersion !== seenSearchVersion.current) {
      seenSearchVersion.current = searchVersion;
      setMode('table');
    }
  }, [searchVersion]);

  // 事件（queryClassAndSetCalendar）：title=course.name、start/end=classBegin/EndDateTimeString、
  // color 依 kind（clazzList_js.jsp:280-296／clazzQueryList.js:1201-1235）。種子無
  // classBeginDateTimeString 的列不會出現在月曆（誠實標註：mock 列由種子補齊，新增列的日期
  // 為文字輸入，格式不對就不上曆）。
  const events = useMemo(
    () =>
      rows
        .map((row) => ({
          // id 經 toKebab（row.id 原值大寫 COU-114-xxxx；uiId 慣例 kebab）。
          id: toKebab(String(row.id ?? '')),
          title: String(row['title'] ?? ''),
          start: String(row['classBeginDateTimeString'] ?? ''),
          backgroundColor:
            String(row['kind'] ?? '') === 'meeting'
              ? COURSE_CALENDAR_MEETING_COLOR
              : COURSE_CALENDAR_CLASS_COLOR,
        }))
        .filter((event) => event.start !== ''),
    [rows],
  );
  const rowsById = useMemo(
    () => new Map(rows.map((row) => [toKebab(String(row.id ?? '')), row])),
    [rows],
  );
  const behavior = pageBehavior(ctx.pageId);
  const hasCustomItem = behavior?.calendarItemRenderer !== undefined;

  function displayCalendar(): void {
    // legacy 點行事曆檢視：先跨月檢查（不通過＝alert、不切換），通過＝以目前查詢表單值重查
    // 並切到行事曆（clazzList_js.jsp:156-164／clazzQueryList.js:76-82 queryClassAndSetCalendar）。
    if (!courseCalendarQueryCheck(beginDate, endDate, ctx.dialogs)) return;
    setMode('calendar');
    ctx.applyQueryValues?.({ beginDate, endDate });
  }

  function displayTable(): void {
    // 列表鈕（雛形自帶切換）＝legacy #btnQuery 同義：切回列表並以目前查詢表單值重查
    // （clazzList_js.jsp:165-168）。
    setMode('table');
    ctx.applyQueryValues?.({ beginDate, endDate });
  }

  // viewRender 鏡像：月檢視起訖寫回查詢表單並重查（LightCalendar 初次渲染＋換月/今天都會呼叫，
  // 對應 fullCalendar viewRender＋getFullCalendarStartDateAndEndDate month 分支；
  // clazzList_js.jsp:2862-2868／classUtils.js:340-345）。
  function calendarViewChange(viewBegin: string, viewEnd: string): void {
    ctx.applyQueryValues?.({ beginDate: viewBegin, endDate: viewEnd });
  }

  return (
    <div data-ui-id={`${ctx.pageId}-display`}>
      <div data-ui-id={`${ctx.pageId}-display-header`} className="mb-2 flex gap-2">
        <Button
          uiId={`${ctx.pageId}-display-calendar`}
          variant={mode === 'calendar' ? 'primary' : 'default'}
          size="sm"
          type="button"
          onClick={displayCalendar}
        >
          行事曆檢視
        </Button>
        <Button
          uiId={`${ctx.pageId}-display-table`}
          variant={mode === 'table' ? 'primary' : 'default'}
          size="sm"
          type="button"
          onClick={displayTable}
        >
          列表
        </Button>
      </div>
      {mode === 'calendar' ? (
        <LightCalendar
          uiId={`${ctx.pageId}-calendar`}
          events={events}
          initialDate={beginDate}
          onViewChange={calendarViewChange}
          onEventClick={
            hasCustomItem
              ? undefined
              : (event) => {
                  // clazzList_js.jsp:2853-2855 eventClick → showClass(calEvent.course.id)。
                  const row = rowsById.get(event.id);
                  if (row !== undefined) ctx.openEdit(row);
                }
          }
          renderEvent={
            hasCustomItem
              ? (event) => {
                  // 批次 11（B2-3）：課程查詢 eventClick → CourseModal.openModal(id,'courseQuery')
                  // （clazzQueryList.js:1176-1178）；以行事曆項目元素呈現（保留既有 uiId）。
                  const row = rowsById.get(event.id);
                  return row === undefined ? null : behavior!.calendarItemRenderer!(row, ctx);
                }
              : undefined
          }
          legend={
            // eventAfterAllRender 的圖例（clazzList_js.jsp:2856-2861）：
            // 「 課程 、 會議」，desc-course/desc-meeting 色字。
            <span data-ui-id={`${ctx.pageId}-calendar-legend`} className="text-[13px]">
              {' '}
              <span
                data-ui-id={`${ctx.pageId}-calendar-legend-course`}
                style={{ color: COURSE_CALENDAR_CLASS_COLOR }}
              >
                課程
              </span>
              {' 、 '}
              <span
                data-ui-id={`${ctx.pageId}-calendar-legend-meeting`}
                style={{ color: COURSE_CALENDAR_MEETING_COLOR }}
              >
                會議
              </span>
            </span>
          }
        />
      ) : (
        table
      )}
    </div>
  );
}

/**
 * 批次刪除（clazzList_js.jsp:325-356）：勾選 chkDelClass 後批次刪；無勾選 alert
 * 「請至少選取一筆資料」（:335）、confirm「是否確定要刪除？」（:338）。
 */
function ClassBatchDeleteButton({ ctx }: { ctx: BehaviorFlowContext }) {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  return (
    <Button
      uiId={`${ctx.pageId}-batch-delete`}
      variant="danger"
      size="sm"
      type="button"
      dataEffect={`remove:${ctx.pageId}`}
      onClick={() => {
        void (async () => {
          const ids = ctx.selection?.ids('batchDelete') ?? [];
          if (ids.length === 0) {
            await dialogs.alert('請至少選取一筆資料');
            return;
          }
          const confirmed = await dialogs.confirm('是否確定要刪除？');
          if (!confirmed) {
            return;
          }
          try {
            // legacy 一次 POST 逗號串接（deleteClassBatchAjax {ids}）；雛形逐筆 remove（同一 op）。
            await dialogs.processing.wrap(
              Promise.all(ids.map((id) => dataSource.remove(ctx.pageId, id))),
            );
            ctx.selection?.clear();
            ctx.reload();
            await dialogs.info(t('state.saveSuccess'));
          } catch (cause: unknown) {
            await dialogs.alert(legacyAlertFallback(cause));
          }
        })();
      }}
    >
      批次刪除
    </Button>
  );
}

/** course-clazz-list（開課管理，origin clazzList.jsp＋clazzList_js.jsp）。 */
const COURSE_CLAZZ_LIST_BEHAVIOR: PageBehavior = {
  // 批次 39（GNL-005-c，issue #39 修復清單）：匯入課程區在查詢 form 內（clazzList.jsp:152-170）。
  importBlockInForm: (ctx) => <ImportToolbar ctx={ctx} spec={importSpecFor('course-clazz-list')} />,
  suppressRowEdit: true, // 操作欄只有刪除＋複製（clazzList_js.jsp:76-100）；修改掛在課程名稱連結
  editLinkKeys: ['title'], // 名稱欄＝.showEdit → showClass（:97-100）
  deleteConfirm: '是否確定要刪除？', // clazzList_js.jsp:366 全形問號
  rowExtraActions: (row, ctx) => [<CopyClassAction key="copy" row={row} ctx={ctx} />],
  selectionBuckets: ['batchDelete'],
  cellOverride: (row, columnKey, ctx) => {
    if (columnKey === 'actions2') {
      // 每列第 2 欄 checkbox name="chkDelClass"（clazzList_js.jsp:85-91）。
      return <AccountCheckCell row={row} ctx={ctx} bucket="batchDelete" visible={true} />;
    }
    return null;
  },
  bulkActions: (ctx) => <ClassBatchDeleteButton ctx={ctx} />,
  tableArea: (children, ctx, rows) => <CourseCalendarToggle table={children} ctx={ctx} rows={rows} />,
  modalTitle: (mode) => (mode === 'create' ? '新增課程' : '修改課程'), // clazzList_js.jsp:17-18
  // 批次 11（B2-1）：課程大 modal（#dialogModal，clazzList.jsp:219-897）——67 欄＋表單控制區＋
  // 學員/教師/教材/人員子流程；通用模板 modal 不再使用（批次 10 editModal hook 先例）。
  editModal: (props) => (
    <ClassEditModal
      pageId={props.pageId}
      mode={props.mode}
      row={props.row}
      onClose={props.onClose}
      onClosed={props.onClosed}
    />
  ),
  editModalTriggerId: 'course-clazz-list-class-dialog', // D-3 自報
};

/** course-course-type（課程類別維護，origin courseType.jsp）。 */
const COURSE_TYPE_BEHAVIOR: PageBehavior = {
  suppressRowEdit: true, // courseType.jsp:20-27 操作欄只有刪除；修改掛在課程類別名稱連結（:48-51）
  editLinkKeys: ['category4'], // 課程類別名稱欄＝.showEdit
  // courseType.jsp:22-25 刪除條件（isPermitDelete && erasable==1 && isEnable==1）。
  deleteVisible: (row) => String(row['erasable'] ?? '') === '1' && String(row['status'] ?? '') === '啟用',
  toggleVisible: () => false, // 切換連結畫在狀態欄（cellOverride）
  usageCheck: {
    disableUsed: '類別已被使用，停用後將不可再使用此類別或此所屬類別下所有類別，是否確定要停用?', // courseType.jsp:326
    disableUnused: '停用後將不可再使用此類別或此所屬類別下所有類別，是否確定要停用?', // courseType.jsp:333
    enable: '是否確定要啟用?', // courseType.jsp:343
    deleteUsed: '此類別已被使用，不能刪除，請改成停用', // courseType.jsp:381
    deleteConfirm: '將刪除此類別或此所屬類別下所有類別，是否確定要刪除?', // courseType.jsp:299
  },
  cellOverride: (row, columnKey, ctx) => {
    if (columnKey === 'status') {
      return (
        <StatusToggleCell
          row={row}
          columnKey={columnKey}
          ctx={ctx}
          texts={COURSE_TYPE_BEHAVIOR.usageCheck as UsageCheckTexts}
          // courseType.jsp:82-90：切換連結掛在 isPermitDelete && erasable==1 下。
          canToggle={(target) => String(target['erasable'] ?? '') === '1'}
        />
      );
    }
    return null;
  },
  modalColumns: (columns) => {
    // courseType.jsp:506-560 modal 欄位：類型/醫院/訓練類別/部門/所屬類別/名稱/排序序號。
    // 醫院/部門的動態連動（getLevel0CourseTypeListAjax 等）雛形不畫，記進度檔已知差異。
    const out: PageColumn[] = [];
    for (const column of columns) {
      if (column.key === 'category2') {
        out.push({ ...column, label: '類型', kind: 'status' as const, pool: ['必修', '選修'] });
      } else if (column.key === 'category') {
        out.push({ ...column, label: '訓練類別', kind: 'status' as const, pool: ['西醫UGY', '牙醫UGY', 'PGY', '醫事UGY'] });
      } else if (column.key === 'category3') {
        out.push({ ...column, label: '所屬類別', kind: 'status' as const, pool: ['無', '急救訓練', '醫學倫理', '感染管制', '高級心臟救命術'] });
      } else if (column.key === 'category4') {
        out.push({ ...column, label: '課程類別名稱', kind: 'text' as const });
      } else if (column.key === 'score') {
        out.push({ ...column, label: '排序序號' });
      }
    }
    return out;
  },
  modalTitle: (mode) => (mode === 'create' ? '新增課程類別' : '修改課程類別'), // courseType.jsp:13-14
  modalReadOnly: (row) => String(row['status'] ?? '') === '停用', // courseType.jsp:191-197
};

/** course-course-manage（課程樣版維護，origin courseManage.jsp）。 */
const COURSE_MANAGE_BEHAVIOR: PageBehavior = {
  suppressRowEdit: true, // courseManage.jsp:41-47 操作欄只有刪除；修改掛在樣版名稱連結（:68）
  editLinkKeys: ['title'], // 課程樣版名稱欄＝.showEdit
  // courseManage.jsp:41 刪除條件（isPermitDelete && erasable==1 && isEnable==1）。
  deleteVisible: (row) => String(row['erasable'] ?? '') === '1' && String(row['status'] ?? '') === '啟用',
  toggleVisible: () => false,
  usageCheck: {
    disableUsed: '課程樣板已被使用，停用後將不可再使用此課程樣板，是否確定要停用?', // courseManage.jsp:563
    disableUnused: '停用後將不可再使用此課程樣板，是否確定要停用?', // courseManage.jsp:570
    enable: '是否確定要啟用?', // courseManage.jsp:581
    deleteUsed: '課程樣板已被使用，不能刪除，請改成停用', // courseManage.jsp:596
  },
  cellOverride: (row, columnKey, ctx) => {
    if (columnKey === 'status') {
      return (
        <StatusToggleCell
          row={row}
          columnKey={columnKey}
          ctx={ctx}
          texts={COURSE_MANAGE_BEHAVIOR.usageCheck as UsageCheckTexts}
          // courseManage.jsp:79-86：切換連結掛在 isPermitDelete && erasable==1 下。
          canToggle={(target) => String(target['erasable'] ?? '') === '1'}
        />
      );
    }
    return null;
  },
  modalTitle: (mode) => (mode === 'create' ? '新增課程樣版' : '修改課程樣版'), // courseManage.jsp:26-27
  modalReadOnly: (row) => String(row['status'] ?? '') === '停用', // courseManage.jsp:279-285 同款模式
};

/** course-course-static-list（開課統計，origin course/courseStaticList.jsp 395 行）。批次 24（#26）。 */
const COURSE_STATIC_LIST_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <CourseStaticListBody pageId={pageId} />,
  complexBodySubmitId: 'course-course-static-list-query-btn', // doQuery :142 → course/queryCourseStaticListAjax
};

/** course-clazz-query-list（課程查詢，origin clazzQueryList.jsp＋clazzQueryList.js）。 */
const COURSE_QUERY_BEHAVIOR: PageBehavior = {
  // 批次 40（GNL-005-c 修復 2/3，issue #39）：infoContent 說明 ul（clazzQueryList.jsp:183 空 div＋
  // clazzQueryList.js:820 JS 注入「本功能僅呈現未來一個月…」）＝在 queryForm 內（diff form[1]/ul[13]）。
  importBlockInForm: () => (
    <ul>
      <li>本功能僅呈現未來一個月被安排的訓練科部所開設的開放報名課程，其它的課程請至 課程專區-&gt;課程查詢 查詢</li>
    </ul>
  ),
  // 名稱連結開「課程資訊」唯讀 modal（不是修改）；無列刪除（契約 rowActions 空）。
  suppressRowEdit: true,
  deleteVisible: () => false,
  toggleVisible: () => false,
  cellOverride: (row, columnKey, ctx) => {
    if (columnKey === 'title') {
      return <ClassQueryTitleCell row={row} ctx={ctx} />;
    }
    if (columnKey === 'status') {
      // 點名欄：報名/退選＋報到＋點名（clazzQueryList.js:886-1197 renderRollCallStatus 的雛形版）。
      return <ClassQueryStatusCell row={row} ctx={ctx} />;
    }
    if (columnKey === 'text') {
      // 公告欄 → 課堂公告 modal。
      return <ClassQueryModalButton row={row} ctx={ctx} columnKey="announcement" label="公告" dialogId="announcement-dialog" />;
    }
    if (columnKey === 'text2') {
      // 表單欄 → 表單明細 modal。
      return <ClassQueryModalButton row={row} ctx={ctx} columnKey="forms" label="表單" dialogId="form-list-dialog" />;
    }
    return null;
  },
  tableArea: (children, ctx, rows) => (
    // 批次 11（B2-6）：首頁模式包住行事曆/列表切換（關閉＝原樣；開啟＝標題/說明/一個月窗過濾清單）。
    <CourseQueryTableArea ctx={ctx} rows={rows}>
      <CourseCalendarToggle table={children} ctx={ctx} rows={rows} />
    </CourseQueryTableArea>
  ),
  // 批次 11（B2-3）：行事曆活動點擊＝檢視課程聚合 modal（clazzQueryList.js:1177）。
  calendarItemRenderer: (row, ctx) => <CourseViewCalendarItem row={row} ctx={ctx} />,
};

/**
 * 批次 23（#25）：form/formTemplate 餘頁 4 頁（complexBody 整頁置換；批次 13/15/17 先例）。
 * form-template-form-preview／form-template-form-template-list-process-form-fields／
 * form-template-set-form-fields／form-template-set-form-flow 為 0 欄參數頁（B4-2 群）不進本批。
 */

/** form-all-form-list（綜合表單查詢，origin form/allFormList.jsp 460 行）。 */
const FORM_ALL_LIST_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <AllFormListBody pageId={pageId} />,
  // 查詢鈕（C5-h 送出自報；doQuery allFormList.jsp:248 → queryFormListAjax）。
  complexBodySubmitId: 'form-all-form-list-query-btn',
};

/** form-form-static-list（表單統計，origin form/formStaticList.jsp 315 行）。 */
const FORM_STATIC_LIST_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <FormStaticListBody pageId={pageId} />,
  complexBodySubmitId: 'form-form-static-list-query-btn', // doQuery formStaticList.jsp:96 → queryFormStaticListAjax
};

/** form-template-common-form-template（共用表單設定，origin formTemplate/commonFormTemplate.jsp 353 行）。 */
const COMMON_FORM_TEMPLATE_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <CommonFormTemplateBody pageId={pageId} />,
  complexBodySubmitId: 'form-template-common-form-template-query-btn', // doQuery :100 → getCommonFormTemplateListAjax
};

/** form-template-form-template-list（表單設計，origin formTemplate/formTemplateList.jsp 418 行；display 頁型）。 */
const FORM_TEMPLATE_LIST_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <FormTemplateListBody pageId={pageId} />,
  complexBodySubmitId: 'form-template-form-template-list-query-btn', // doQuery :213 → getFormTemplateDetailListAjax
};

/** form-plan-form-batch-send（批次發送表單，origin form/planFormBatchSend.jsp 818 行）。批次 23 Wave 2。 */
const PLAN_FORM_BATCH_SEND_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <PlanFormBatchSendBody pageId={pageId} />,
  // 下一步＝getStudents（:637 → form/batch/getFormStudentListAjax；C5-h 送出自報）。
  complexBodySubmitId: 'form-plan-form-batch-send-next-step-btn',
};

/** form-template-form-template（表單精靈，origin formTemplate/formTemplate.jsp 727 行）。批次 23 Wave 3（誠實殼：設計器不鏡像＝D23-13）。 */
const FORM_TEMPLATE_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <FormTemplateBody pageId={pageId} />,
  // 存檔（doSaveFormTemplate → update；C5-h 送出自報）。
  complexBodySubmitId: 'form-template-form-template-save-btn',
};

/** form-template-field-type-list（常用題型格式維護，origin formTemplate/fieldTypeList.jsp 253 行）。批次 23 Wave 3。 */
const FIELD_TYPE_LIST_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <FieldTypeListBody pageId={pageId} />,
  complexBodySubmitId: 'form-template-field-type-list-query-btn', // doQuery :101
};

/** exam-question-bank（題庫管理，origin questionBank.jsp）。 */
const QUESTION_BANK_BEHAVIOR: PageBehavior = {
  editLinkKeys: ['title'], // 題庫名稱＝.showEdit（questionBank.jsp:62-66）
  // 刪除連結條件（questionBank.jsp:39-47：isEnable==1 && erasable==1）；
  // 確認文字依題數（:365-368：cnt>0 →「此題庫內尚有題目，確定要刪除嗎?」）。
  deleteVisible: (row) => String(row['status'] ?? '') === '啟用' && String(row['erasable'] ?? '') === '1',
  deleteConfirm: (row) =>
    Number(row['cnt'] ?? 0) > 0 ? '此題庫內尚有題目，確定要刪除嗎?' : '是否確定要刪除?',
  toggleVisible: () => false, // 切換連結畫在狀態欄（cellOverride）
  // 批次 45（#9，issue #9）：編輯欄導流入口（questionBank.jsp:70-76 a.showQuestion 編輯題目
  // → :119 window.location.href=exam/question/{id}＝參數相依頁 exam-question-manage 的唯一入口；
  // 站內導航鏡像＝B19-2 先例）。
  cellOverride: (row, columnKey, ctx) => {
    if (columnKey === 'text') {
      return <QuestionBankEditQuestionLink row={row} pageId={ctx.pageId} />;
    }
    if (columnKey === 'status') {
      return (
        <StatusToggleCell
          row={row}
          columnKey={columnKey}
          ctx={ctx}
          texts={QUESTION_BANK_TEXTS}
          // questionBank.jsp:86-99：切換連結掛在 row.erasable==1 下。
          canToggle={(target) => String(target['erasable'] ?? '') === '1'}
        />
      );
    }
    if (columnKey === 'actions2') {
      // 第 2 個空欄＝複製鈕（questionBank.jsp:48-56 cloneBtn，isEnable==1 才有）。
      return <CloneQuestionBankButton row={row} ctx={ctx} />;
    }
    return null;
  },
  modalTitle: (mode) => (mode === 'create' ? '新增題庫' : '修改題庫'), // questionBank.jsp:13-14
  // crud-fix（2026-10-04）：qb 兩段式誠實殼——legacy nextBtn=save(0) 成功後
  // showInitImportModal()（modal-title「新增題目」，匯入/手動兩選）；react 存檔成功提示後
  // 開同標題唯讀殼（內容＋關閉鈕由 CrudListPage 渲染；真題目維護＝#19 範疇）。
  // 只有新增模式（mode==='create'）回第二步（legacy 修改/複製走 save(1)/(2)＝查詢＋提示，P29）。
  postSaveModal: (input) =>
    input.mode === 'create'
      ? {
          title: '新增題目', // questionBank.jsp initImportModal dialogModalLabel 逐字
          note: '題目維護（匯入／手動）屬真後端範疇（#19）；此為兩段式第二步的誠實殼（存檔已生效，關閉回到清單）。',
        }
      : undefined,
};

/**
 * questionBank 的啟停確認文字：legacy 停用的 confirm 文字來自後端
 * （validateQuestionBankStatusAjax 的 response.desc，questionBank.jsp:139-152）；
 * 雛形以固定文字鏡像啟用分支的「是否確定要啟用」（:149，無問號）風格。
 */
const QUESTION_BANK_TEXTS: UsageCheckTexts = {
  disableUsed: '是否確定要停用',
  disableUnused: '是否確定要停用',
  enable: '是否確定要啟用', // questionBank.jsp:153 逐字（無問號）
  deleteUsed: '此題庫已被使用，不能刪除，請改成停用', // 題庫刪除無 usage alert，此條僅備位
};

/** 題庫複製鈕（questionBank.jsp:48-56 cloneBtn → cloneQuestionBank 複製整庫）。 */
function CloneQuestionBankButton({ row, ctx }: { row: Row; ctx: BehaviorFlowContext }) {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const [busy, setBusy] = useState(false);
  const enabled = String(row['status'] ?? '') === '啟用';
  if (!enabled) {
    return null;
  }
  return (
    <Button
      uiId={`${ctx.pageId}-row-${toKebab(String(row.id ?? ''))}-clone`}
      variant="default"
      size="sm"
      type="button"
      disabled={busy}
      dataEffect={`create:${ctx.pageId}`}
      onClick={() => {
        void (async () => {
          setBusy(true);
          try {
            const { id: _omit, ...cloneSource } = row;
            const created = await dialogs.processing.wrap(dataSource.create(ctx.pageId, cloneSource));
            ctx.reload();
            await dialogs.info(t('state.saveSuccess'));
            ctx.openEdit(created);
          } catch (cause: unknown) {
            await dialogs.alert(legacyAlertFallback(cause));
          } finally {
            setBusy(false);
          }
        })();
      }}
    >
      複製
    </Button>
  );
}

/** exam-question-bank-type（題庫類型維護，origin questionBankType.jsp）：只覆寫 modal 標題。 */
const QUESTION_BANK_TYPE_BEHAVIOR: PageBehavior = {
  modalTitle: (mode) => (mode === 'create' ? '新增題庫類型' : '修改題庫類型'), // questionBankType.jsp:13-14
};

/** exam-exam-manage（試卷維護，origin examManage.jsp）。 */
const EXAM_MANAGE_BEHAVIOR: PageBehavior = {
  editLinkKeys: ['title'], // 試卷名稱＝.showEdit（examManage.jsp:54）
  deleteVisible: () => true, // examManage.jsp:41-47：刪除只掛權限，無列條件
  modalTitle: (mode) => (mode === 'create' ? '新增試卷' : '修改試卷'), // examManage.jsp:28-29
  // 批次 13（B3-1）：試卷大 modal（#dialogModal，examManage.jsp:982-1192）——23 欄＋題組維護
  // （#examGroupModal）＋選題（#produceQuestionDiv）＋自動配分；通用模板 modal 不再使用
  // （批次 10 editModal hook 先例）。
  editModal: (props) => (
    <ExamEditModal
      pageId={props.pageId}
      mode={props.mode}
      row={props.row}
      onClose={props.onClose}
      onClosed={props.onClosed}
    />
  ),
  editModalTriggerId: 'exam-exam-manage-exam-dialog', // D-3 自報
};

/** exam-dispatch-exam（試卷派送，origin dispatchExam.jsp）。 */
const EXAM_DISPATCH_BEHAVIOR: PageBehavior = {
  // 批次 13（B3-3）：整頁自訂 body（ComplexFormPage 不消費行為——批次 3 的
  // selectionBuckets/cellOverride 在真實路由下是死碼，本批以 complexBody 取代）；
  // 查詢表單＋listTable 六欄（勾選欄 INIT/ANSWERED 隱）＋分派 modal 六段驗證鏈。
  complexBody: (props) => <DispatchExamBody pageId={props.pageId} />,
  complexBodySubmitId: 'exam-dispatch-exam-dispatch-query-btn', // 查詢鈕（C5-h 送出自報）
};

// ---------------------------------------------------------------------------
// 批次 14（Gitea issue #22）：report 模組 W1 匯出/download 頁群 10 頁
// （reportBody＝ReportPage 整頁置換；legacy 無結果表——匯出即 fileDownload，雛形
// 不落地檔案；驗證鏈/payload 見 ReportExportBodies.tsx 檔頭逐頁行號）。
// ---------------------------------------------------------------------------
const CLASS_FORM_EXPORT_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <ClassFormExportBody pageId={props.pageId} />,
  reportBodySubmitId: 'report-common-class-form-export-export-btn',
};
const CLAZZ_ATTENDANCE_EXPORT_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <ClazzAttendanceExportBody pageId={props.pageId} />,
  reportBodySubmitId: 'report-common-clazz-attendance-export-export-btn',
};
const CLAZZ_DETAIL_EXPORT_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <ClazzDetailExportBody pageId={props.pageId} />,
  reportBodySubmitId: 'report-common-clazz-detail-export-export-btn',
};
const PLAN_FORM_EXPORT_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <PlanFormExportBody pageId={props.pageId} />,
  reportBodySubmitId: 'report-common-plan-form-export-export-btn',
};
const ALL_STUDENT_SCORES_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <AllStudentScoresBody pageId={props.pageId} />,
  reportBodySubmitId: 'report-pgywm-all-student-scores-export-btn',
};
const GPA_FORM_REPORT_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <GpaFormExportBody pageId={props.pageId} />,
  reportBodySubmitId: 'report-tsgh-gpa-form-report-export-btn',
};
const COURSE_FORM_FINISHRATE_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <CourseFormFinishrateBody pageId={props.pageId} />,
  reportBodySubmitId: 'report-ugywm-course-form-finishrate-export-btn',
};
const STUDENT_DEPT_SATISFY_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <StudentDeptSatisfyBody pageId={props.pageId} />,
  reportBodySubmitId: 'report-ugywm-student-dept-satisfy-export-btn',
};
const CORE_SKILL_FORM_REPORT_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <CoreSkillFormReportBody pageId={props.pageId} />,
  reportBodySubmitId: 'report-tsgh-core-skill-form-report-export-btn',
};
const CORE_SKILL_SUTDENT_REPORT_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <CoreSkillSutdentReportBody pageId={props.pageId} />,
  reportBodySubmitId: 'report-tsgh-core-skill-sutdent-report-export-btn',
};

// 批次 14 W2：查詢統計頁群 10 頁（查詢＝list filters 真打；結果表＝種子列；列印＝提示）。
const LOGIN_STATIC_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <LoginStaticBody pageId={props.pageId} />,
  reportBodySubmitId: 'report-tsgh-login-static-query-btn',
};
const CLASS_FORM_STATIC_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <ClassFormStaticBody pageId={props.pageId} />,
  reportBodySubmitId: 'report-tsgh-class-form-static-query-btn',
};
const CLASS_FORM_STATIC_7_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <ClassFormStatic7Body pageId={props.pageId} />,
  reportBodySubmitId: 'report-tsgh-class-form-static-to-class-form-7-query-btn',
};
const PLAN_FORM_STATIC_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <PlanFormStaticBody pageId={props.pageId} />,
  reportBodySubmitId: 'report-tsgh-plan-form-static-query-btn',
};
const PLAN_FORM_STATIC_5_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <PlanFormStatic5Body pageId={props.pageId} />,
  reportBodySubmitId: 'report-tsgh-plan-form-static-to-plan-form-5-query-btn',
};
const PLAN_FORM_STATIC_2_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <PlanFormStatic2Body pageId={props.pageId} />,
  reportBodySubmitId: 'report-tsgh-plan-form-static-2-query-btn',
};
const PLAN_FORM_STATIC_9_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <PlanFormStatic9Body pageId={props.pageId} />,
  reportBodySubmitId: 'report-tsgh-plan-form-static-2-to-plan-form-9-query-btn',
};
const PLAN_FORM_STATIC_10_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <PlanFormStatic10Body pageId={props.pageId} />,
  reportBodySubmitId: 'report-tsgh-plan-form-static-2-to-plan-form-10-query-btn',
};
const CORE_SKILL_REPORT_BY_FORM_STAT_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <CoreSkillReportByFormStatBody pageId={props.pageId} />,
  reportBodySubmitId: 'report-tsgh-core-skill-report-by-form-query-btn',
};
const CORE_SKILL_REPORT_BY_STUDENT_STAT_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <CoreSkillReportByStudentStatBody pageId={props.pageId} />,
  reportBodySubmitId: 'report-tsgh-core-skill-report-by-student-query-btn',
};

// 批次 14 W3：特殊頁群 3 頁。
const CORE_SKILL_STATISTIC_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <CoreSkillStatisticBody pageId={props.pageId} />,
  reportBodySubmitId: 'report-tsgh-core-skill-statistic-query-btn',
};
const CORE_SKILL_STATISTIC_REPORT_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <CoreSkillStatisticReportBody pageId={props.pageId} />,
  // 無查詢鈕（列印頁）——C5-h 送出鈕自報為列印鈕（頁面無 list 呼叫，見 harness 對
  // 無 payload 頁的處理：此頁契約欄僅 csrf（已排除）＝分母 0，不會被 TARGETS 收錄）。
  reportBodySubmitId: 'report-tsgh-core-skill-statistic-report-print-btn',
};
const ACADEMIC_MOM_BEHAVIOR: PageBehavior = {
  reportBody: (props) => <AcademicMomBody pageId={props.pageId} />,
  reportBodySubmitId: 'report-tsgh-academic-mom-query-btn',
};

/**
 * 表單查詢工具列（js-my/formList.js：doUpdateFormStatus:210/doDeleteForm:161/
 * reopenFormFlow:364/doInformFiller:309）。選列＝formCheckBox 勾選（第一欄，表頭有全選）。
 * 啟停/刪除/變更未完成＝真打 update/remove；通知填寫＝mock 提示（B1-3 模式，不帶 data-effect）。
 */
function FormListBulkToolbar({ ctx }: { ctx: BehaviorFlowContext }) {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const navigate = useNavigate();
  const doneOf = (id: string) => ctx.doneLookup?.(id) === true;
  const selectedIds = () => ctx.selection?.ids('form') ?? [];
  // 批次 44（#42）：列印（planFormList.jsp:710 showPrintForms）。legacy 填 ids→POST
  // target=_blank；雛形以站內導航鏡像（批次 41 前例；printForms 頁 0 契約參數頁）。
  const printFlow = () => {
    void (async () => {
      const ids = selectedIds();
      if (ids.length === 0) {
        await dialogs.alert('未選擇表單!');
        return;
      }
      navigate('/form/prompt/printForms');
    })();
  };
  const statusFlow = (word: '啟用' | '停用') => {
    void (async () => {
      const ids = selectedIds();
      if (ids.length === 0) {
        await dialogs.alert('未選擇表單!');
        return;
      }
      // formList.js:245/:250 逐字（複驗 F2）：已啟用再啟用／已停用再停用 = 操作錯誤。
      const target = word === '啟用' ? '1' : '0';
      const wrongState = ids.filter((id) => ctx.enabledLookup?.(id) === (target === '1'));
      if (wrongState.length > 0) {
        await dialogs.alert(
          word === '啟用' ? '操作錯誤!您選到已經是啟用狀態的表單!' : '操作錯誤!您選到已經是停用狀態的表單!',
        );
        return;
      }
      const doneQty = ids.filter((id) => doneOf(id)).length;
      // legacy msg 是 HTML（js-my/formList.js:229-235）；confirm 只收字串，以換行呈現同訊息。
      const confirmed = await dialogs.confirm(
        `${doneQty > 0 ? `${word}[已完成]的表單數量: ${String(doneQty)}\n\n` : ''}${word}表單總數量: ${String(ids.length)}\n\n是否確定要${word}表單?`,
      );
      if (!confirmed) {
        return;
      }
      try {
        // legacy updateFormStatusAjax（啟停表單可用性）；雛形以 enabled 旗標逐筆 update。
        await dialogs.processing.wrap(
          Promise.all(ids.map((id) => dataSource.update(ctx.pageId, id, { enabled: word === '啟用' ? '1' : '0' }))),
        );
        ctx.selection?.clear();
        ctx.reload();
        await dialogs.info(t('state.saveSuccess'));
      } catch (cause: unknown) {
        await dialogs.alert(legacyAlertFallback(cause));
      }
    })();
  };
  const deleteFlow = () => {
    void (async () => {
      const ids = selectedIds();
      if (ids.length === 0) {
        await dialogs.alert('未選擇表單!');
        return;
      }
      const doneQty = ids.filter((id) => doneOf(id)).length;
      const confirmed = await dialogs.confirm(
        `${doneQty > 0 ? `刪除[已完成]的表單數量: ${String(doneQty)}\n\n` : ''}刪除表單總數量: ${String(ids.length)}\n\n是否確定要刪除表單?`,
      );
      if (!confirmed) {
        return;
      }
      try {
        await dialogs.processing.wrap(Promise.all(ids.map((id) => dataSource.remove(ctx.pageId, id))));
        ctx.selection?.clear();
        ctx.reload();
        await dialogs.info(t('state.saveSuccess'));
      } catch (cause: unknown) {
        await dialogs.alert(legacyAlertFallback(cause));
      }
    })();
  };
  const reopenFlow = () => {
    void (async () => {
      const ids = selectedIds();
      if (ids.some((id) => doneOf(id) === false)) {
        await dialogs.alert('錯誤! 已完成的表單才能改為未完成!');
        return;
      }
      if (ids.length === 0) {
        await dialogs.alert('錯誤! 未選擇表單!');
        return;
      }
      const confirmed = await dialogs.confirm('是否確定要變更表單為未完成?');
      if (!confirmed) {
        return;
      }
      try {
        await dialogs.processing.wrap(
          Promise.all(ids.map((id) => dataSource.update(ctx.pageId, id, { done: '0' }))),
        );
        ctx.selection?.clear();
        ctx.reload();
        await dialogs.info(t('state.saveSuccess'));
      } catch (cause: unknown) {
        await dialogs.alert(legacyAlertFallback(cause));
      }
    })();
  };
  const informFlow = () => {
    void (async () => {
      const ids = selectedIds();
      if (ids.some((id) => doneOf(id) === true)) {
        await dialogs.alert('已完成的表單不需要發送通知，請重新選擇!');
        return;
      }
      if (ids.length === 0) {
        await dialogs.alert('未選擇表單!');
        return;
      }
      // formList.js:332 逐字（複驗 F1）。
      const confirmed = await dialogs.confirm('是否確定要發送填寫通知?');
      if (!confirmed) {
        return;
      }
      // legacy POST form/informFillFormAjax（js-my/formList.js:309-330）；雛形不送真通知（B1-3）。
      await dialogs.info(t('state.saveSuccess'));
    })();
  };
  return (
    <span data-ui-id={`${ctx.pageId}-form-bulk-actions`} className="inline-flex flex-wrap items-center gap-2">
      {/* 批次 44（#42）：列印（planFormList.jsp:710；dataEffect 未映射＝不帶）。 */}
      <Button
        uiId={`${ctx.pageId}-form-print`}
        variant="default"
        size="sm"
        type="button"
        onClick={printFlow}
      >
        列印
      </Button>
      <Button
        uiId={`${ctx.pageId}-form-bulk-enable`}
        variant="default"
        size="sm"
        type="button"
        dataEffect={`update:${ctx.pageId}`}
        onClick={() => {
          statusFlow('啟用');
        }}
      >
        啟用
      </Button>
      <Button
        uiId={`${ctx.pageId}-form-bulk-disable`}
        variant="default"
        size="sm"
        type="button"
        dataEffect={`update:${ctx.pageId}`}
        onClick={() => {
          statusFlow('停用');
        }}
      >
        停用
      </Button>
      <Button
        uiId={`${ctx.pageId}-form-bulk-delete`}
        variant="danger"
        size="sm"
        type="button"
        dataEffect={`remove:${ctx.pageId}`}
        onClick={deleteFlow}
      >
        刪除
      </Button>
      <Button
        uiId={`${ctx.pageId}-form-bulk-reopen`}
        variant="default"
        size="sm"
        type="button"
        dataEffect={`update:${ctx.pageId}`}
        onClick={reopenFlow}
      >
        變更為未完成
      </Button>
      <Button
        uiId={`${ctx.pageId}-form-bulk-inform`}
        variant="danger"
        size="sm"
        type="button"
        onClick={informFlow}
      >
        通知填寫
      </Button>
      {/* 批次 44（#42）：移除原型「全選」鈕（legacy planFormList.jsp:709-721 無此鈕；
          表頭 form-select-all＝theadSelectAll 接手，批次 41 同款裁決）。 */}
    </span>
  );
}

/**
 * 表單查詢雙頁共用行為（form-class-form-list 課程表單查詢／form-plan-form-list 訓練計畫表單）。
 */
const FORM_LIST_BEHAVIOR: PageBehavior = {
  suppressRowEdit: true, // 名稱連結＝showFillForm 開填寫頁（參數相依頁，B4-1）；無列修改/刪除
  // 批次 45（#10）：名稱欄畫連結（行為層 linkNavigate 接手＝導流非 openEdit）。
  editLinkKeys: ['title'],
  // 批次 44（#42）：表頭全選（planFormList.jsp:741 id=form-select-all）。
  theadSelectAll: 'form-select-all',
  // 批次 44（#42）：ids 屬 printForm 表單（planFormList.jsp:811-813），不在查詢 form；
  // 契約面保留（payload 照帶）＝批次 41 渲染排除前例。
  // 批次 50（工作項 1）：formStatus radio×3 同 name 去重（legacy-fields 規則 1）只畫 1 顆
  // （value 變瀏覽器預設 'on'）＝b49-triage 案 2 H4（缺 radio×2＋多 label/input value=on＋
  // 文 5 位移）。渲染排除＋enableStatus 之後附掛自畫（queryInlineExtras.enableStatus；
  // 比照批次 41 ClassFormListBody 的 radio 群 DOM。complexBody 整頁置換會把批次 44 plan
  // 專屬增補全數搬移，侵入過大不取；class-form-list 已 complexBody 早退，本排除對其為死碼）。
  queryFieldExclusions: ['ids', 'formStatus'],
  // 批次 44（#42）：reset 圓鈕（:639 訓練期間組／:654 填表者組；icon-only 空文字按鈕）。
  // 批次 50（工作項 1）：enableStatus 欄位之後附掛「表單進行狀態」radio 群
  // （planFormList.jsp:679-686；afterFieldNodes＝渲染在該欄位之後，契約序列 enableStatus
  // 是排除後的最後一欄 → radio 群 → queryTailFields 的 showMyForm＝legacy 順序 :669-706）。
  queryInlineExtras: (ctx, onChange) => {
    // 受控草稿值（CrudListPage flowCtx.queryValues＝draftFieldValues；初始 ''＝「全部」checked）。
    const formStatus = ctx.queryValues?.['formStatus'] ?? '';
    return {
      trainingEndDate: (
        <button
          type="button"
          data-ui-id={`${ctx.pageId}-query-dates-reset`}
          aria-label="清除訓練期間"
          className="inline-block size-8 rounded-full border border-border text-ink hover:bg-surface-subtle disabled:cursor-not-allowed disabled:opacity-[.65]"
          onClick={() => {
            onChange('trainingStartDate', '');
            onChange('trainingEndDate', '');
          }}
        />
      ),
      fillerId: (
        <button
          type="button"
          data-ui-id={`${ctx.pageId}-query-filler-reset`}
          aria-label="清除填表者"
          className="inline-block size-8 rounded-full border border-border text-ink hover:bg-surface-subtle disabled:cursor-not-allowed disabled:opacity-[.65]"
          onClick={() => {
            onChange('searchFillerCode', '');
            onChange('fillerId', '');
          }}
        />
      ),
      // 表單進行狀態 radio×3（:682-684）：value 語意鏡像 legacy（'' 全部 checked／'false'
      // 進行中／'true' 已完成）＝任務 b50「不 hack 序列化、以 legacy value 語意鏡像為準」；
      // onChange 只寫草稿（查詢按鈕才生效＝queryInlineExtras 既有語意；doQuery :494 送
      // formStatus=checked 值）。DOM 形狀照批次 41 先例（label 包 input＋前綴空格文字）。
      enableStatus: (
        <div
          className="flex flex-wrap items-center gap-2"
          style={FULL_ROW_STYLE}
          data-ui-id={`${ctx.pageId}-search-form-status-group`}
        >
          <label className={FIELD_LABEL_CLASS}>表單進行狀態：</label>
          <label className="inline-flex items-center gap-1 text-sm text-ink">
            <input
              type="radio"
              name="formStatus"
              value=""
              checked={formStatus === ''}
              onChange={() => {
                onChange('formStatus', '');
              }}
              className="size-4 accent-brand"
            />
            {' 全部'}
          </label>
          <label className="inline-flex items-center gap-1 text-sm text-ink">
            <input
              type="radio"
              name="formStatus"
              value="false"
              checked={formStatus === 'false'}
              onChange={() => {
                onChange('formStatus', 'false');
              }}
              className="size-4 accent-brand"
            />
            {' 進行中'}
          </label>
          <label className="inline-flex items-center gap-1 text-sm text-ink">
            <input
              type="radio"
              name="formStatus"
              value="true"
              checked={formStatus === 'true'}
              onChange={() => {
                onChange('formStatus', 'true');
              }}
              className="size-4 accent-brand"
            />
            {' 已完成'}
          </label>
        </div>
      ),
    };
  },
  // 批次 44（#42）：自己待填的表單（:702-704；showMyForm 無 name＝契約抽不到＝行為屬補）。
  queryTailFields: (ctx) => (
    <>
      <label className="mb-[5px] inline-block font-bold text-ink">自己待填的表單：</label>
      <input
        id="showMyForm"
        type="checkbox"
        data-ui-id={`${ctx.pageId}-search-show-my-form`}
        className="size-4 accent-brand"
      />
      {/* biome-ignore lint/a11y/noLabelWithoutControl: 逐字鏡像 legacy 裸文字（:704 無 label 包裹）。 */}
      <label htmlFor="showMyForm" className="text-sm text-ink">
        (只顯示自己待填的表單)
      </label>
    </>
  ),
  // 批次 44（#42）：列印 form 在表格後（action=form/prompt/printForms target=_blank＋ids
  // hidden＝勾選列；csrf 照 Q2(e) 不畫）。
  tableArea: (children, ctx) => (
    <>
      {children}
      <form
        action="/form/prompt/printForms"
        method="POST"
        target="_blank"
        data-ui-id={`${ctx.pageId}-print-form`}
        onSubmit={(event) => {
          event.preventDefault();
        }}
      >
        <input
          type="hidden"
          name="ids"
          value={(ctx.selection?.ids('form') ?? []).join(',')}
          onChange={() => undefined}
          data-ui-id={`${ctx.pageId}-print-ids`}
        />
      </form>
    </>
  ),
  deleteVisible: () => false,
  toggleVisible: () => false,
  selectionBuckets: ['form'],
  cellOverride: (row, columnKey, ctx) => {
    if (columnKey === 'actions') {
      // 第一個空欄＝formCheckBox 勾選欄（classFormList.jsp:70、表頭全選 :525）。
      return <AccountCheckCell row={row} ctx={ctx} bucket="form" visible={true} />;
    }
    return null;
  },
  // 批次 45（#10 B4-1，issue #10）：名稱連結＝showFillForm 導流（formList.js:113-122：
  // window.open form/prompt/fillForm?isWindowOpen=true&formKey=…&fromPage=…&refTable=…&refId=…；
  // 雛形以站內導航鏡像＝B19-2 先例；新視窗語意差＝導航後頁面為 0 契約參數頁＝誠實殼）。
  linkNavigate: (row) =>
    `/form/prompt/fillForm?isWindowOpen=true&formKey=${encodeURIComponent(String(row['formKey'] ?? ''))}&fromPage=planForm&refTable=${encodeURIComponent(String(row['refTable'] ?? ''))}&refId=${encodeURIComponent(String(row['refId'] ?? ''))}`,
  // 批次 44（#42）：按鈕列改查詢鈕後（planFormList.jsp:709-721 查詢在前）＝searchTailActions。
  searchTailActions: (ctx) => <FormListBulkToolbar ctx={ctx} />,
};

/**
 * 批次 41（#39 修復 3/3，issue #39）：form-class-form-list 整頁 complexBody。
 * 緣起：契約以 name 為 key 去重（legacy-fields 規則 1）——formStatus radio×3 同 variantKey
 * 只畫 1 顆、showMyForm 無 name 被抽出器丟掉，泛用 SearchPanel/LegacyContractFields
 * 無法表達；裁決（使用者 2026-09-30）：complexBody 化（自寫查詢區＋沿用表格殼；
 * 批次 15/24 前例；不動 generated 紅線）。詳 ClassFormListBody.tsx 檔頭。
 * 下方展開的 FORM_LIST_BEHAVIOR 欄位在 complexBody 早退下是真實路由死碼（dispatch-exam
 * 先例），保留以備回退；form-plan-form-list 仍走 FORM_LIST_BEHAVIOR（本批不動）。
 */
const CLASS_FORM_LIST_BEHAVIOR: PageBehavior = {
  ...FORM_LIST_BEHAVIOR,
  complexBody: ({ pageId }) => <ClassFormListBody pageId={pageId} />,
  complexBodySubmitId: 'form-class-form-list-search-submit', // 查詢鈕（C5-h 送出自報；值經 list filters 進 DataSource）
};

/** form-plan-form-dispatch（訓練計畫手動補發，origin planFormDispatch.jsp）。 */
const FORM_DISPATCH_BEHAVIOR: PageBehavior = {
  bulkActions: (ctx) => <DispatchPhaseToolbar ctx={ctx} />,
};

/**
 * 補發工具列（planFormDispatch.jsp:11-40）：目標日期（可空＝依日期查當日應發）；
 * dispatchPhaseForm／dispatchPhaseExam 端點為批次派送，雛形不真派（B1-3 模式，不帶 data-effect）。
 */
function DispatchPhaseToolbar({ ctx }: { ctx: BehaviorFlowContext }) {
  const dialogs = useDialogs();
  const [targetDate, setTargetDate] = useState('');
  const run = (kind: 'form' | 'exam-monthly' | 'exam') => {
    void (async () => {
      // planFormDispatch.jsp 實為三鈕（複驗 F3）：補發計畫表單（不需日期 :65）、
      // 補發計畫表單(每月_號用)（需日期，未輸入日期! :12-14）、補發計畫測驗（不需日期 :110）。
      if (kind === 'exam-monthly' && targetDate.trim() === '') {
        await dialogs.alert('未輸入日期!');
        return;
      }
      await dialogs.processing.wrap(new Promise<void>((resolve) => {
        setTimeout(resolve, 0);
      }));
      await dialogs.info(
        kind === 'form' ? '補發計畫表單完成' : kind === 'exam-monthly' ? '補發計畫表單(每月_號用)完成' : '補發計畫測驗完成',
      );
    })();
  };
  return (
    <span data-ui-id={`${ctx.pageId}-dispatch-toolbar`} className="inline-flex flex-wrap items-center gap-2">
      <Button
        uiId={`${ctx.pageId}-dispatch-form`}
        variant="primary"
        size="sm"
        type="button"
        onClick={() => {
          run('form');
        }}
      >
        補發計畫表單
      </Button>
      <label className="text-sm" htmlFor={`${ctx.pageId}-dispatch-target-date`}>目標日期：</label>
      <TextInput
        uiId={`${ctx.pageId}-dispatch-target-date`}
        value={targetDate}
        onChange={setTargetDate}
      />
      <Button
        uiId={`${ctx.pageId}-dispatch-form-monthly`}
        variant="primary"
        size="sm"
        type="button"
        onClick={() => {
          run('exam-monthly');
        }}
      >
        補發計畫表單(每月_號用)
      </Button>
      <Button
        uiId={`${ctx.pageId}-dispatch-exam`}
        variant="primary"
        size="sm"
        type="button"
        onClick={() => {
          run('exam');
        }}
      >
        補發計畫測驗
      </Button>
    </span>
  );
}

/** leave-leave-type（請假類別設定，origin leaveType.jsp）。 */
/** 批次 45（#11 B5-2）：設定學習年度（leaveAnnual.jsp:136-165 驗證鏈＋dialogModal students）。 */
const LEAVE_ANNUAL_BEHAVIOR: PageBehavior = {
  // 學員勾選（:131-135 studentCheckBox page:'all 跨頁收集）；表頭全選。
  selectionBuckets: ['students'],
  theadSelectAll: true,
  cellOverride: (row, columnKey, ctx) => {
    if (columnKey === 'actions') {
      return <AccountCheckCell row={row} ctx={ctx} bucket="students" visible={true} />;
    }
    return null;
  },
  // dialogForm 開啟時 students hidden 播種＝勾選學員 JSON（:168 students=JSON.stringify）。
  modalFieldSeed: (ctx) => ({
    students: JSON.stringify(ctx.selection?.ids('students') ?? []),
  }),
  // 驗證鏈（:136-165 逐條）：未選學員→第1年起迄 required→第1~3年日期序。
  modalSubmitGuard: (_spec, payload) => {
    const students = payload['students'] ?? '';
    if (students === '' || students === '[]') return '請先選取要設定的學員'; // :137
    if ((payload['startDate1'] ?? '') === '' || (payload['endDate1'] ?? '') === '') {
      return '請至少輸入第1年的起迄時間'; // :148
    }
    for (const year of [1, 2, 3] as const) {
      const s = payload[`startDate${year}`] ?? '';
      const e = payload[`endDate${year}`] ?? '';
      // isAfterOrSame（:152/:157/:162）：起 > 迄＝不正確；yyyy/MM/dd 字串序可比。
      if (s !== '' && e !== '' && s > e) return `第${year}年日期不正確!`; // :153/:158/:163
    }
    return null;
  },
};

const LEAVE_TYPE_BEHAVIOR: PageBehavior = {
  editLinkKeys: ['category', 'category2'], // 請假類別＋訓類類別皆＝.showEdit（leaveType.jsp:26-31，複驗 F7）
  suppressRowEdit: true, // OP 欄只有刪除（:15-24）
  deleteVisible: (row) =>
    String(row['erasable'] ?? '') === '1' && String(row['isEnable'] ?? '') === '啟用', // leaveType.jsp:16-24
  toggleVisible: () => false, // 切換連結畫在「操作」欄（cellOverride）
  cellOverride: (row, columnKey, ctx) => {
    if (columnKey === 'text') {
      // 「操作」欄＝isEnable 值＋啟停連結（leaveType.jsp:53-66）。
      return <StatusToggleCell row={row} columnKey="isEnable" ctx={ctx} texts={LEAVE_TYPE_TEXTS} canToggle={() => true} />;
    }
    return null;
  },
};

/**
 * leave-leave-form 操作欄儲存格（legacy formTable 每列：檢視＋刪除，isPermitDelete 只看 SysAdmin；
 * leaveForm.js initDatatable 操作欄 render＋deleteForm 的 sysConfirm）。mock 無角色 → 兩鈕恆顯。
 * uiId 與模板 rowActions 同型（-edit／-delete），rowAction 引擎不需改。
 */
function LeaveRowActionsCell({ row, ctx }: { row: Row; ctx: BehaviorFlowContext }) {
  const dialogs = useDialogs();
  const dataSource = useDataSource();
  const key = toUiKey(String(row.id ?? ''));
  return (
    <span data-ui-id={`${ctx.pageId}-result-row-${key}-actions`} className="inline-flex gap-1">
      <Button
        uiId={`${ctx.pageId}-result-row-${key}-edit`}
        variant="default"
        size="sm"
        type="button"
        onClick={() => {
          ctx.openEdit(row);
        }}
      >
        {/* legacy＝檢視（viewLeaveForm）；既有模板語意是修改鈕，標籤差已登記 */}
        {t('action.edit')}
      </Button>
      <Button
        uiId={`${ctx.pageId}-result-row-${key}-delete`}
        variant="danger"
        size="sm"
        type="button"
        dataEffect={`remove:${ctx.pageId}`}
        onClick={() => {
          void (async () => {
            // 與模板 requestDelete 同鏈（deleteConfirm 文案同 resolver；P37 文案逐字同）。
            const confirmed = await dialogs.confirm(
              resolveLegacyMessageForAction(ctx.pageId, 'confirm', 'delete')?.text ?? t('confirm.delete'),
            );
            if (!confirmed) {
              return;
            }
            try {
              await dialogs.processing.wrap(dataSource.remove(ctx.pageId, String(row.id ?? '')));
              ctx.reload();
            } catch {
              await dialogs.alert(
                resolveLegacyMessageForAction(ctx.pageId, 'failure', 'delete')?.text ?? t('error.generic'),
              );
            }
          })();
        }}
      >
        {t('action.delete')}
      </Button>
    </span>
  );
}

/**
 * leave-leave-form（假單作業，origin leaveForm.jsp；leave-fix 2026-10-04）：
 * 請假申請＝legacy 專用 modal（#dialogModal #dialogForm：學員資料 label 區＋假別 select2＋
 * 起~迄日期＋時段 select＋時數計算＋到逹地點/說明＋簽核流程表），非表格欄直映的泛用
 * form-dialog（11 欄含假欄位 text2「操作」＝舊行為，本批淘汰）。實作＝批次 10 editModal hook
 * 先例（AccountV2DialogModal 同款），並沿用 LegacyContractModals 慣例：D-3 trigger 自報
 * （editModalTriggerId）、送出鈕 data-effect="create:<pageId>" 真打 DataSource（C5-j）、
 * 隱藏欄值一定進 payload、欄位 name 沿用 legacy（D-2）。驗證鏈在 LeaveFormDialogModal
 * 內（leaveForm.js saveNewForm 逐條，modal 層級＝批次 10 先例）。
 * 列操作語意：legacy 每列是「檢視/刪除」（無修改）→ editModal 開唯讀檢視 modal
 * （viewForm：status>=0/-1 全欄位 disabled）；操作鈕文字沿用既有「修改」（標籤差已登記）。
 */
const LEAVE_FORM_BEHAVIOR: PageBehavior = {
  addLabel: '請假申請', // leaveForm.jsp `<button class="btn btn-add" onclick="LeaveForm.showForm(0,null);">請假申請</button>`
  editModal: (props) => <LeaveFormDialogModal {...props} />,
  editModalTriggerId: 'leave-leave-form-leave-dialog', // D-3 自報（LeaveFormDialogModal 的 uiId）
  cellOverride: (row, columnKey, ctx) => {
    // 「操作」欄（表頭有字，非空字串欄＝推斷規則的 text2）＝每列操作（legacy 操作欄位置）。
    if (columnKey === 'text2') {
      return <LeaveRowActionsCell row={row} ctx={ctx} />;
    }
    // 第一欄（學年度）不再掛行動作（按鈕移回操作欄＝legacy 位置；取消模板的
    // 「無操作欄時 inline 掛第一欄」fallback，否则雙份按鈕）。
    if (columnKey === 'date') {
      return <>{String(row['date'] ?? '')}</>;
    }
    return null;
  },
};

/** leaveType updateStatus 的 confirm 文字（leaveType.jsp:359 sysConfirm(str)）。 */
const LEAVE_TYPE_TEXTS: UsageCheckTexts = {
  disableUsed: '是否確定要停用?',
  disableUnused: '是否確定要停用?',
  enable: '是否確定要啟用?',
  deleteUsed: '是否確定要刪除?',
};

/**
 * 留言內容檢視 modal（discussBoard.jsp 主題連結 .showEdit :106-110 → #discussBoard :781）。
 * legacy 的公開/隱藏切換也在這個 modal 內（btnStatus，:303-311，閘門 isManagePermitUpdate）；
 * 表格本身沒有切換欄（複驗 F2/F3）。
 */
function DiscussContentModal({
  row,
  ctx,
  onClose,
}: {
  row: Row;
  ctx: BehaviorFlowContext;
  onClose(): void;
}) {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const status = String(row['status'] ?? '公開');
  const next = status === '公開' ? '隱藏' : '公開';
  return (
    <ModalFrame
      uiId={`${ctx.pageId}-content-dialog`}
      open
      title="留言內容"
      size="lg"
      onClose={onClose}
      footer={
        <>
          {String(row['erasable'] ?? '') === '1' ? (
            <Button
              uiId={`${ctx.pageId}-content-dialog-toggle`}
              variant="default"
              size="sm"
              type="button"
              dataEffect={`update:${ctx.pageId}`}
              onClick={() => {
                void (async () => {
                  // discussBoard.jsp:311 逐字。
                  const confirmed = await dialogs.confirm(`將${next}此討論串，是否確定要執行?`);
                  if (!confirmed) {
                    return;
                  }
                  try {
                    await dialogs.processing.wrap(
                      dataSource.update(ctx.pageId, String(row.id ?? ''), { status: next }),
                    );
                    ctx.reload();
                    await dialogs.info(t('state.saveSuccess'));
                    onClose();
                  } catch (cause: unknown) {
                    await dialogs.alert(legacyAlertFallback(cause));
                  }
                })();
              }}
            >
              {next}
            </Button>
          ) : null}
          <Button
            uiId={`${ctx.pageId}-content-dialog-cancel`}
            variant="default"
            size="sm"
            type="button"
            onClick={onClose}
          >
            {t('action.close')}
          </Button>
        </>
      }
    >
      <div data-ui-id={`${ctx.pageId}-content-dialog-body`} className="space-y-1 text-sm">
        <div data-ui-id={`${ctx.pageId}-content-dialog-subject`}>
          <span className="inline-block w-20 font-medium">主題：</span>
          {String(row['title'] ?? '')}
        </div>
        <div>
          <span className="inline-block w-20 font-medium">留言者：</span>
          {String(row['person'] ?? '')}
        </div>
        <div>
          <span className="inline-block w-20 font-medium">狀態：</span>
          {status}
        </div>
      </div>
    </ModalFrame>
  );
}

/** 主題連結（discussBoard.jsp :71 .showEdit）→ 留言內容檢視 modal。 */
function DiscussSubjectCell({ row, ctx }: { row: Row; ctx: BehaviorFlowContext }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-ui-id={`${ctx.pageId}-result-row-${toKebab(String(row.id ?? ''))}-title-link`}
        className={`${LINK_TEXT_CLASS} cursor-pointer`}
        data-modal-trigger={`${ctx.pageId}-content-dialog`}
        onClick={() => {
          setOpen(true);
        }}
      >
        {String(row['title'] ?? '')}
      </button>
      {open ? (
        <DiscussContentModal
          row={row}
          ctx={ctx}
          onClose={() => {
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

/** announcement-announcement-list（公告，origin announcementList.jsp）。 */
const ANNOUNCEMENT_BEHAVIOR: PageBehavior = {
  suppressRowEdit: true, // OP 欄只有刪除（announcementList.jsp:25-33，無列條件掛權限）
  editLinkKeys: ['title'], // 主旨＝.showEdit（:48）
  deleteVisible: () => true,
  deleteConfirm: '是否確定要刪除?', // announcementList.jsp:528
};

/** discuss-discuss-board（留言板，origin discussBoard.jsp）。 */
const DISCUSS_BOARD_BEHAVIOR: PageBehavior = {
  suppressRowEdit: true, // OP 欄只有刪除（:58-64）；主題連結＝留言內容檢視 modal（複驗 F3）
  deleteVisible: (row) =>
    String(row['erasable'] ?? '') === '1' &&
    String(row['isEnable'] ?? '') === '啟用' &&
    String(row['hasUsage'] ?? '') !== '1', // :59 erasable==1 && isEnable==1 && !hasUsage（複驗 F1：與 status 獨立）
  toggleVisible: () => false,
  cellOverride: (row, columnKey, ctx) => {
    if (columnKey === 'title') {
      return <DiscussSubjectCell row={row} ctx={ctx} />;
    }
    if (columnKey === 'actions2') {
      // 複驗 F2：legacy 表格無公開/隱藏切換欄（切換在留言內容 modal 內）；此欄留空。
      return <span data-ui-id={`${ctx.pageId}-row-${toKebab(String(row.id ?? ''))}-isread`}>{'\u00a0'}</span>;
    }
    return null;
  },
};

/** discuss-discuss-type（留言類別管理，origin discussType.jsp）。 */
const DISCUSS_TYPE_BEHAVIOR: PageBehavior = {
  suppressRowEdit: true, // OP 欄只有刪除（:22-30）；修改掛在留言類別名稱連結（:40）
  editLinkKeys: ['category3'],
  // :25 刪除條件（isPermitDelete && erasable==1 && isEnable==1 && !hasUsage）（複驗 F4）。
  deleteVisible: (row) =>
    String(row['erasable'] ?? '') === '1' &&
    String(row['status'] ?? '') === '啟用' &&
    String(row['hasUsage'] ?? '') !== '1',
  toggleVisible: () => false,
  cellOverride: (row, columnKey, ctx) => {
    if (columnKey === 'status') {
      return (
        <StatusToggleCell
          row={row}
          columnKey={columnKey}
          ctx={ctx}
          texts={DISCUSS_TYPE_TEXTS}
          // 複驗 F5：啟用連結（停用列）需 erasable==1 && isParentEnable（:49）；
          // 停用連結（啟用列）只需 erasable==1（:55）。
          canToggle={(target) =>
            String(target['erasable'] ?? '') === '1' &&
            (String(target['status'] ?? '') === '啟用' || String(target['isParentEnable'] ?? '') === '1')
          }
        />
      );
    }
    return null;
  },
};
const DISCUSS_TYPE_TEXTS: UsageCheckTexts = {
  // 複驗 F6：停用 confirm 為 client-side 組字（discussType.jsp:259-267）。
  disableUsed: '類別已被使用，停用後將不可再使用此類別，是否確定要停用?',
  disableUnused: '停用後將不可再使用此類別，是否確定要停用?',
  enable: '是否確定要啟用?', // :267
  deleteUsed: '是否確定要刪除?',
}; // discussType.jsp:240 刪除 confirm 逐字；啟停 confirm 見上（client-side 組字）

// 批次 15（B7-1，Gitea issue #13）→ 批次 49（b49-planinst）：訓練期程
// （training-plan-inst-plan-inst-list，origin training/planInst/planInstList.jsp＋
// planInstFormSearchPlanInst.jsp＋planInstList_js.jsp）complexBody 整頁置換。
// 緣起：泛用 CrudListPage＋本行為（批次 15 B7-1：editModal/cellOverride/bulkActions/
// rowExpanded）已能渲染查詢 form＋主表＋明細，但 103 筆結構差的根源是泛用模板表達不了的
// legacy 形狀——label-per-group（學員/導師/臨床教師群一 label 對 input＋select）、無 name
// 的 searchClinicalNo、panel 層 btnAdd（form 外）、獨立 importForm、departmentId 的
// hidden form-group、dynamic select 初始態（115/本機帳號/系統管理＋6 個 0 options）、
// 隱藏範本×4（詳 PlanInstListBody.tsx 檔頭；規格 b49-planinst-spec.md §0/§3，Lead 十點
// 裁定照執行；OPTION_SET_OVERRIDES 機制見 legacy-fields.ts）。裁決（規格 §8-1，批次 41
// 同準則）：complexBody 化。
// 原 hooks 在 complexBody 早退下是真實路由死碼（dispatch-exam 批次 24 先例）：渲染責任
// 已搬進 PlanInstListBody（cellOverride→renderCell／bulkActions→importForm 內的
// PlanInstImportToolbar／rowExpanded→Table expandedRowRender／editModal→body 直掛
// PlanInstEditModal）；trigger uiId 不變（-plan-inst-dialog，toolbar-add 的
// data-modal-trigger 自報，批次 15 測試相容）。
const PLAN_INST_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <PlanInstListBody pageId={pageId} />,
  complexBodySubmitId: 'training-plan-inst-plan-inst-list-search-submit', // 查詢鈕（C5-h 送出自報）
};

// 批次 15 Wave 2（B7-2，issue #13）：學習紀錄填寫（training-study-record-study-record-fill，origin
// training/studyRecord/studyRecordFill.jsp＋fillChoiceModal.jsp＋studyRecordFormModal.jsp＋
// recordFormTemplate.jsp＋targetDetailStatus.jsp，親讀 2026-09-24）。
// 主表 9 欄無操作欄：填寫選擇欄（text）＝跨填 checkbox／填寫鈕；已填明細（text2）＝details-control。
const STUDY_RECORD_FILL_BEHAVIOR: PageBehavior = {
  // 跨填勾選桶（tgCheckBox，fill :54）；一次填寫多類（fillCross fillChoiceModal.jsp:27-58）對勾選列驗證。
  selectionBuckets: ['fill'],
  // 批次 43：一次填寫多類鈕＋說明＝legacy form[1] 頂部（:243-247）＝searchTopActions
  // （原 bulkActions 位置在查詢鈕前列＝R-diff 順序不同 2 筆的根因）。
  searchTopActions: (ctx) => <StudyRecordFillToolbar ctx={ctx} />,
  // 查詢暫存或待填＋暫存清單（doQueryDraft fill:161-190；雛形＝種子，0 data-effect）。
  tableArea: (children, ctx) => <StudyRecordDraftArea pageId={ctx.pageId}>{children}</StudyRecordDraftArea>,
  cellOverride: (row, columnKey, ctx) => {
    const uiKey = toUiKey(String(row.id ?? ''));
    if (columnKey === 'text') {
      // 填寫選擇（fill :49-56）：isCrossTargetFill==1→tgCheckBox（跨填）；否則 填寫鈕(:56)→fillOne(:99)。
      if (String(row['isCrossTargetFill'] ?? '') === '1') {
        return (
          <Checkbox
            uiId={`${ctx.pageId}-row-${uiKey}-tg-check`}
            // legacy name=tgCheckBox（fillCross 以 name 收值）；雛形以 selection API 讀值
            // （非 serialize 路徑）→ 不帶 name（C5-h 分母只計帶 name 控制項；批次 14 先例）。
            checked={ctx.selection?.has('fill', String(row.id ?? '')) === true}
            onChange={() => ctx.selection?.toggle('fill', String(row.id ?? ''))}
          />
        );
      }
      return <FillChoiceCellOpener pageId={ctx.pageId} row={row} uiKey={uiKey} />;
    }
    if (columnKey === 'text2') {
      return <DetailExpanderCell pageId={ctx.pageId} row={row} uiKey={uiKey} ctx={ctx} />;
    }
    if (columnKey === 'title') {
      return <>{targetLabelOf(row)}</>; // 學習目標 `{name} ({formTemplateId})`（:63-66）
    }
    if (columnKey === 'score') {
      return <>{targetQtyOf(row)}</>; // 目標數量（:67-78）
    }
    if (columnKey === 'score2') {
      return <DoneStatusOpener pageId={ctx.pageId} row={row} uiKey={uiKey} />;
    }
    return null;
  },
  // 已填明細展開（recordFormTemplate.jsp；fill 頁無 變更填寫者）。
  rowExpanded: (row, ctx) => (
    <StudyRecordDetailRows
      pageId={ctx.pageId}
      row={row}
      uiKey={toUiKey(String(row.id ?? ''))}
      ctx={ctx}
      isStatus={false}
    />
  ),
};

// 批次 15 Wave 2（B7-2）：學習紀錄查詢（training-study-record-study-record-status，origin
// studyRecordStatus.jsp）。主表 10 欄無操作欄：已填明細（text）＝details-control＋變更填寫者。
const STUDY_RECORD_STATUS_BEHAVIOR: PageBehavior = {
  cellOverride: (row, columnKey, ctx) => {
    const uiKey = toUiKey(String(row.id ?? ''));
    if (columnKey === 'text') {
      // 已填明細（details-control；:128-136 展開→getStudyTargetFormList(:119)）。
      return <DetailExpanderCell pageId={ctx.pageId} row={row} uiKey={uiKey} ctx={ctx} />;
    }
    if (columnKey === 'title2') {
      return <>{targetLabelOf(row)}</>; // 學習目標（:65-68）
    }
    if (columnKey === 'score') {
      return <>{targetQtyOf(row)}</>; // 目標數量（:69-80）
    }
    if (columnKey === 'score2') {
      return <DoneStatusOpener pageId={ctx.pageId} row={row} uiKey={uiKey} />;
    }
    return null;
  },
  // 已填明細展開＋變更填寫者（isPermitUpdate；changeFillerModal.jsp）。
  rowExpanded: (row, ctx) => (
    <StudyRecordDetailRows
      pageId={ctx.pageId}
      row={row}
      uiKey={toUiKey(String(row.id ?? ''))}
      ctx={ctx}
      isStatus={true}
    />
  ),
};

// 批次 15 Wave 3（B7-3，issue #13）：成績單／教師安排（complex-form 頁型，complexBody 整頁置換，
// 批次 13 先例；complexBodySubmitId 自報＝查詢鈕，C5-h 送出）。
const TRANSCRIPT_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <TranscriptBody pageId={pageId} />,
  complexBodySubmitId: 'training-transcript-list-query-btn',
};

const ARRANGE_TRAIN_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <ArrangeTrainBody pageId={pageId} />,
  complexBodySubmitId: 'training-arrange-train-query-btn',
};

// 批次 15 Wave 3（B7-4）：訓練計畫（instruction-plan-target-plan-target-list）。
// {{name}} 列連結＝計畫名稱欄 a.editInstrunctionPlan→showModalPhaseTarget modifyPlan
// （planTargetList_js.jsp:91-96/:122-127）；大 modal 群另冊專批，雛形＝通用修改 modal。
// 操作欄：刪除被註解（planTargetList_js.jsp:39）；複製（copyInstrunctionPlan :42/:133-138）→openCreate 帶列值【雛形值】。
const PLAN_TARGET_BEHAVIOR: PageBehavior = {
  editLinkKeys: ['title'], // 計畫名稱欄＝修改連結
  suppressRowEdit: true, // 操作欄無修改（修改在名稱連結）
  deleteVisible: () => false, // 刪除被註解（planTargetList_js.jsp:35-38）
  rowExtraActions: (row, ctx) => [
    <Button
      key="copy"
      uiId={`${ctx.pageId}-row-${toUiKey(String(row.id ?? ''))}-copy`}
      variant="default"
      size="sm"
      type="button"
      onClick={() =>
        ctx.openCreate({
          date: String(row['date'] ?? ''),
          unit: String(row['unit'] ?? ''),
          unit2: String(row['unit2'] ?? ''),
          title: String(row['title'] ?? ''),
          title2: String(row['title2'] ?? ''),
        })
      }
    >
      複製
    </Button>,
  ],
};

/**
 * 批次 16（B8-2，Gitea issue #15）：個人基本資料維護（profile-profile-maintenance）。
 * 整頁自訂 body（ProfileMaintenanceBody：單筆表單＋證照子清單＋證照 modal），
 * 掛 complexBody（批次 13 hook 先例；CrudListPage 已於批次 16 補 complexBody 早退）。
 * 存檔＝dataSource.update（endpoint-map update:profile-profile-maintenance→
 * /profile/saveMyAccountAjax）；詳 docs/plans/2026-09-25-batch16-profile-plan.md。
 */
const PROFILE_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <ProfileMaintenanceBody pageId={pageId} />,
  // C5-h 送出自報（D-10）：存檔鈕（profileMaintenance.jsp:286-290 btn-add onclick=save()）。
  complexBodySubmitId: 'profile-profile-maintenance-actions-save',
};

/**
 * 批次 17（B8-3，Gitea issue #16）：投票維護（vote-vote-maintenance，/vote/main）。
 * complexBody 整頁置換（VoteMaintenanceBody：查詢＋匯入醫師名單區＋8 欄表＋三 modal＋
 * 啟停/刪除/存檔/匯入/匯出/範本）；存檔＝create/update、刪除＝remove、查詢＝list；
 * 詳 docs/plans/2026-09-25-batch17-vote-setting-plan.md §2。
 */
const VOTE_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <VoteMaintenanceBody pageId={pageId} />,
  // C5-h 送出自報（D-10）：查詢鈕（jsp:121 #query；filters 進 list）。
  complexBodySubmitId: 'vote-vote-maintenance-query-btn',
};

/**
 * 批次 19（B8-1，Gitea issue #14）：dashboard 頁型（main-* 首頁 8 變體）。
 * dashboardBody 整頁置換（DashboardBody：menuRow 盒群＋公告欄＋訓練資訊 panel＋行事曆 modal；
 * 契約 0 欄非契約驅動，資料層＝dashboard-store 種子）。8 個 pageId 共用同一 behavior
 * （變體差異在 DASHBOARD_VARIANTS 設定表，dashboardConfig.ts）。
 */
const DASHBOARD_BEHAVIOR: PageBehavior = {
  dashboardBody: ({ pageId }) => <DashboardBody pageId={pageId} />,
};

/**
 * 批次 20（admin 首頁專批）：main-main（/main）角色分流（R20-1）。
 * AdminMainBody：admin→DeptAdminBody（MainController.java:174-175 isDepartmentAdmin→deptAdminMain）、
 * teacher/student→DashboardBody（main-teacher-main/main-student-main 變體，variantPageId 鏡像伺服器端
 * 渲染別頁 JSP）；else 分支（main.jsp）不可達→KNOWN-NOT-DONE D20-2。
 */
const MAIN_MAIN_BEHAVIOR: PageBehavior = {
  dashboardBody: ({ pageId }) => <AdminMainBody pageId={pageId} />,
};

/**
 * 批次 20：main-dept-admin-main（/main/departmentAdminMain，MainController.java:108-112
 * @RequiresRoles("DeptAdmin")）＝DeptAdminBody（deptAdminMain.jsp＋deptAdminMain.js 659 行行為鏈；
 * 資料層＝dashboard-store deptAdmin 種子群；教師名單安排 modal＝ClinicalArrangementModal）。
 */
const DEPT_ADMIN_BEHAVIOR: PageBehavior = {
  dashboardBody: ({ pageId }) => <DeptAdminBody pageId={pageId} />,
};

/**
 * 批次 20（R20-2，B19-1）：main-medical-affairs-clinical-main（legacy clinicalMain.jsp orphan；
 * clinicalMain.js＝medicalAffairs/teacherMain.js 複本）＝第 9 dashboard 變體，經 unresolvedBody
 * 整頁置換（UnresolvedPage 早退；pageType unresolved 不得改 routes.generated.ts 紅線）。
 */
const CLINICAL_MAIN_BEHAVIOR: PageBehavior = {
  unresolvedBody: ({ pageId }) => <DashboardBody pageId={pageId} />,
};

/**
 * 批次 21（Gitea issue #24，B8-3 未涵蓋）：vote 報表/投票頁群 4 頁。
 * 詳 docs/plans/2026-09-27-batch21-vote-pages-plan.md §1/§2。
 */
/** vote-dept-manager-recommend（/vote/deptManagerRecommend，crud-list）：單位主管推薦
 *  （查詢＋4 欄表＋投票/檢視鈕＋RecommendModal＋送出驗證鏈）。complexBody（批次 16/17 先例）。 */
const DEPT_MANAGER_RECOMMEND_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <DeptManagerRecommendBody pageId={pageId} />,
  // C5-h 送出自報：查詢鈕（jsp:101 #query；filters 以契約 DOM name 直送 list，批次 17
  // voteMaintenance 同款語意）；recommendModal 送出鈕的 update 流程由本批測試覆蓋。
  complexBodySubmitId: 'vote-dept-manager-recommend-query-btn',
};
/** vote-excellent-teacher（/vote/prompt/voteExcellentTeacher，display）：優良臨床教師投票
 *  （role gate→/main 鏡像 criteria==null redirect；投票卡片＋maxVote=ceil(n/2)＋送出）。
 *  complexBody（批次 17 display 頁先例）。 */
const EXCELLENT_TEACHER_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <ExcellentTeacherVoteBody pageId={pageId} />,
  // C5-h 送出自報：頁尾送出鈕（excellentTeacher.jsp:168 #submit）。
  complexBodySubmitId: 'vote-excellent-teacher-submit',
};
/** vote-total-vote-report（/tsghrpt/vote/totalVote，report）：投票總人數（3 selects＋匯出結果）。
 *  reportBody（批次 14/18 先例）。 */
const TOTAL_VOTE_REPORT_BEHAVIOR: PageBehavior = {
  reportBody: ({ pageId }) => <TotalVoteReportBody pageId={pageId} />,
  // C5-h 送出自報：匯出結果鈕（jsp:73 #exportBtn）。
  reportBodySubmitId: 'vote-total-vote-report-export-btn',
};
/** vote-total-vote-analysis-report（/tsghrpt/vote/totalVoteAnalysis，report）：總可被投票數（同形）。 */
const TOTAL_VOTE_ANALYSIS_REPORT_BEHAVIOR: PageBehavior = {
  reportBody: ({ pageId }) => <TotalVoteAnalysisReportBody pageId={pageId} />,
  // C5-h 送出自報：匯出結果鈕（jsp:73 #exportBtn）。
  reportBodySubmitId: 'vote-total-vote-analysis-report-export-btn',
};

/**
 * 批次 17（B8-3）：高風險設定（customer-tsgh-high-risk-setting，/tsghfunc/toHighRiskSetting，
 * display 頁型）。complexBody 整頁置換（HighRiskSettingBody：級聯＋可編輯表 6 欄＋存檔/刪除）。
 */
const HIGH_RISK_SETTING_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <HighRiskSettingBody pageId={pageId} />,
  // C5-h 送出自報：存檔鈕（jsp:233 onclick=doSave()）。
  complexBodySubmitId: 'customer-tsgh-high-risk-setting-actions-save',
};

/**
 * 批次 17（B8-3）：合理醫療服務量設定（customer-tsgh-service-standard-setting，display）。
 * complexBody 整頁置換（ServiceStandardSettingBody：固定評估項目表＋radio N/A／數值＋存檔靜默照實）。
 */
const SERVICE_STANDARD_SETTING_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <ServiceStandardSettingBody pageId={pageId} />,
  // C5-h 送出自報：存檔鈕（jsp:154 onclick=doSave()）。
  complexBodySubmitId: 'customer-tsgh-service-standard-setting-actions-save',
};

/**
 * RF-147：個人化等第制成績設定（instruction-personalization-gpa-score-setting，display，非選單頁）。
 * complexBody 整頁置換（PersonalizationGPASettingBody：7 等第頁籤×13 題單選＋儲存鈕）。
 */
const PERSONALIZATION_GPA_SCORE_SETTING_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <PersonalizationGPASettingBody pageId={pageId} />,
  // C5-h 送出自報：第 1 頁籤（A++）的儲存鈕（personalizationGPATab.jsp:157 onclick=submitForm）。
  complexBodySubmitId: 'instruction-personalization-gpa-score-setting-pane-1-actions-save',
};

/**
 * 批次 18（issue #23）：customer 匯出型 5 頁（report 頁型，reportBody 整頁置換）。
 * 查詢表單＋「匯出」鈕＝$.fileDownload 下載 .xls（legacy 無結果表；批次 14 W1 同款）。
 */
const HIGH_RISK_DOCTOR_REPORT_BEHAVIOR: PageBehavior = {
  reportBody: ({ pageId }) => <HighRiskDoctorReportBody pageId={pageId} />,
  // 匯出鈕（raw :1299 onclick=doQuery()）＝fileDownload downloadHighRiskDoctorReportExcel。
  reportBodySubmitId: 'customer-tsgh-high-risk-doctor-report-export-btn',
};
const HIGH_RISK_REPORT_BEHAVIOR: PageBehavior = {
  reportBody: ({ pageId }) => <HighRiskReportBody pageId={pageId} />,
  // 匯出鈕（raw :1299 onclick=doQuery()）＝fileDownload downloadHighRiskReportExcel。
  reportBodySubmitId: 'customer-tsgh-high-risk-report-export-btn',
};
const HIGH_RISK_SETTING_REPORT_BEHAVIOR: PageBehavior = {
  reportBody: ({ pageId }) => <HighRiskSettingReportBody pageId={pageId} />,
  // 匯出鈕（raw :1268 onclick=doExport()）＝fileDownload downloadHighRiskSetReportExcel。
  reportBodySubmitId: 'customer-tsgh-high-risk-setting-report-export-btn',
};
const SERVICE_STANDARD_CFG_REPORT_BEHAVIOR: PageBehavior = {
  reportBody: ({ pageId }) => <ServiceStandardCfgReportBody pageId={pageId} />,
  // 匯出鈕（raw :1282 onclick=doQuery()）＝fileDownload downloadServiceStandardCfgExcel。
  reportBodySubmitId: 'customer-tsgh-service-standard-cfg-report-export-btn',
};
const SERVICE_STANDARD_REPORT_BEHAVIOR: PageBehavior = {
  reportBody: ({ pageId }) => <ServiceStandardReportBody pageId={pageId} />,
  // 匯出鈕（raw :1299 onclick=doQuery()）＝fileDownload downloadServiceStandardExcel。
  reportBodySubmitId: 'customer-tsgh-service-standard-report-export-btn',
};
const TCH_SATISFACTION_REPORT_BEHAVIOR: PageBehavior = {
  reportBody: ({ pageId }) => <TchSatisfactionSurveyReportBody pageId={pageId} />,
  // 匯出鈕（raw :1332 onclick=doExport()）＝fileDownload /tsghrpt/tchSatisfactionSurveyReport/doExport。
  reportBodySubmitId: 'customer-tsgh-tch-satisfaction-survey-report-export-btn',
};

/** 批次 18 S2：查詢型 2 頁（reportBody 整頁置換；查詢鈕 $.post → 結果表）。 */
const CASE_REPORT_BEHAVIOR: PageBehavior = {
  reportBody: ({ pageId }) => <CaseReportBody pageId={pageId} />,
  // C5-h 送出自報：查詢鈕（raw :1325 onclick=doQuery()＝$.post getCaseReportAjax）。
  reportBodySubmitId: 'customer-tsgh-case-report-query-btn',
};
const STUDENT_COURSE_REPORT_BEHAVIOR: PageBehavior = {
  reportBody: ({ pageId }) => <StudentCourseReportBody pageId={pageId} />,
  // C5-h 送出自報：查詢鈕（raw :1325 onclick=doQuery()＝$.post getStudentCourseReportAjax）。
  reportBodySubmitId: 'customer-tsgh-tsgh-student-course-report-query-btn',
};

/** 批次 18 S3：查詢型複雜 2 頁（reportBody 整頁置換；動態表頭＋modal 明細群）。 */
const CORE_CURRICULUM_REPORT_BEHAVIOR: PageBehavior = {
  reportBody: ({ pageId }) => <CoreCurriculumReportBody pageId={pageId} />,
  // C5-h 送出自報：查詢鈕（raw :1349 onclick=doQuery()＝$.post getCoreCurriculumListAjax）。
  reportBodySubmitId: 'customer-tsgh-core-curriculum-report-query-btn',
};
const COURSE_RECORD_REPORT_BEHAVIOR: PageBehavior = {
  reportBody: ({ pageId }) => <CourseRecordReportBody pageId={pageId} />,
  // C5-h 送出自報：查詢鈕（raw :1346 onclick=doQuery()＝$.post getCourseRecordListAjax）。
  reportBodySubmitId: 'customer-tsgh-course-record-report-query-btn',
};

/** 批次 18 S4：display 2 頁（complexBody 整頁置換；DisplayPage 早退，批次 17 先例）。 */
const CLERT_CERK_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <ClertCerkBody pageId={pageId} />,
  // C5-h 送出自報：查詢鈕（raw :1299 onclick=doQuery()＝$.post getClerkCertListAjax）。
  complexBodySubmitId: 'customer-tsgh-clert-cerk-query-btn',
};
const STUDY_HISTORY_LIST_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <StudyHistoryListBody pageId={pageId} />,
  // C5-h 送出自報：查詢鈕 #btnQuery（raw :1423；查詢端點未映射→照帶＋登記）。
  complexBodySubmitId: 'customer-tsgh-tsgh-study-history-list-query-btn',
};

/**
 * 批次 42（GNL-005-d，issue #40 W12②）：實習證明單樣板維護（display 頁型 complexBody
 * 整頁置換）——兩組 label＋file(accept=".doc, .docx")＋上傳/下載鈕；行為鏡像
 * certTemplateCfg.jsp:14-74 save()/downFile()（見 CertTemplateBody 註解）。
 * 上傳/下載皆死路徑（無真後端）＝不帶 data-effect（批次 9 匯入慣例同款誠實標註）。
 */
const CERT_TEMPLATE_BEHAVIOR: PageBehavior = {
  complexBody: ({ pageId }) => <CertTemplateBody pageId={pageId} />,
};
/** 批次 43（#41）：共同編輯表單查詢行為（queryPage.jsp/queryPage.js；詳 CoEditQueryPageBody 檔頭）。 */
const CO_EDIT_QUERY_PAGE_BEHAVIOR: PageBehavior = {
  complexBody: (props) => <CoEditQueryPageBody pageId={props.pageId} />,
  // C5-h：送出鈕＝查詢（dataEffect list:<pageId>，filters 直送 DataSource）。
  complexBodySubmitId: 'form-co-edit-form-query-page-search-submit',
};

/** 批次 43（#41）：共同編輯表單填寫行為（fillForm.jsp prompt 頁；詳 CoEditFillFormBody 檔頭）。 */
const CO_EDIT_FILL_FORM_BEHAVIOR: PageBehavior = {
  complexBody: (props) => <CoEditFillFormBody pageId={props.pageId} />,
  // C5-h：送出＝form submit（隱藏送出鈕承接；legacy 觸發器在動態內容＝捕捉態不顯現）。
  complexBodySubmitId: 'form-co-edit-form-fill-form-form-submit',
};

/** 批次 43（#41）：表單產生器說明區（formGenerator.jsp:162-182：shiro 群按鈕＋p×2＋textarea）。 */
function FormGeneratorAside({ pageId }: { pageId: string }): ReactElement {
  return (
    <div data-ui-id={`${pageId}-aside`}>
      {'說明:'}
      {/* shiro hasAnyRoles SysAdmin,TeachAdmin（:164-172）；admin 捕捉態＝顯示。 */}
      <Button
        uiId={`${pageId}-aside-edit-desc`}
        variant="default"
        size="sm"
        type="button"
      >
        編輯說明
      </Button>
      {/* 儲存鈕（:168-171 display:none＝隱藏）——raw button（Button 無 className prop；批次 41 空鈕前例）。 */}
      <button
        type="button"
        data-ui-id={`${pageId}-aside-save-desc`}
        className="hidden"
      >
        儲存
      </button>
      {/* p#content（:173）／p#functionDesc（:176）：捕捉態空。 */}
      <p id="content" />
      <p id="functionDesc" />
      {/* desc-textarea（:178 maxlength=100；shiro 群內＝admin 顯示；捕捉態空內容）。 */}
      <textarea id="desc-textarea" maxLength={100} className="block w-full" />
    </div>
  );
}

/** 批次 43（#41）：表單產生器行為（formGenerator.jsp；說明區含互動件＝asideNote hook）。 */
const CO_EDIT_FORM_GENERATOR_BEHAVIOR: PageBehavior = {
  asideNote: ({ pageId }) => <FormGeneratorAside pageId={pageId} />,
};


const REGISTRY: Readonly<Record<string, PageBehavior>> = {
  'config-annual': ANNUAL_BEHAVIOR,
  'config-role': ROLE_BEHAVIOR,
  'config-location': LOCATION_BEHAVIOR,
  'config-department': DEPARTMENT_BEHAVIOR,
  'config-account-v-2': ACCOUNT_BEHAVIOR,
  // 批次 9：匯入專批（config 7 頁特例）新註冊的 3 頁（comment/device/job-title 只掛匯入工具列）。
  'config-comment': COMMENT_BEHAVIOR,
  'config-device': DEVICE_BEHAVIOR,
  'config-job-title': JOB_TITLE_BEHAVIOR,
  // 批次 26（#21）：config 其餘頁行為註冊。
  'config-organization': ORGANIZATION_BEHAVIOR,
  'config-forbid-word': FORBID_WORD_BEHAVIOR,
  'config-job-type': JOB_TYPE_BEHAVIOR,
  'config-job-type-department': JOB_TYPE_DEPARTMENT_BEHAVIOR,

  'course-course-type': COURSE_TYPE_BEHAVIOR,
  // 批次 24（#26）：course 模組唯一有契約但無行為的漏網頁（B2 群 issue #6 未涵蓋）。
  'course-course-static-list': COURSE_STATIC_LIST_BEHAVIOR,
  'course-course-manage': COURSE_MANAGE_BEHAVIOR,
  'course-clazz-list': COURSE_CLAZZ_LIST_BEHAVIOR,
  'course-clazz-query-list': COURSE_QUERY_BEHAVIOR,
  // 批次 23（#25）：form/formTemplate 餘頁 4 頁。
  'form-all-form-list': FORM_ALL_LIST_BEHAVIOR,
  'form-form-static-list': FORM_STATIC_LIST_BEHAVIOR,
  'form-template-common-form-template': COMMON_FORM_TEMPLATE_BEHAVIOR,
  'form-template-form-template-list': FORM_TEMPLATE_LIST_BEHAVIOR,
  'form-plan-form-batch-send': PLAN_FORM_BATCH_SEND_BEHAVIOR,
  'form-template-form-template': FORM_TEMPLATE_BEHAVIOR,
  'form-template-field-type-list': FIELD_TYPE_LIST_BEHAVIOR,
  'course-clazz-query-list-class-query': COURSE_QUERY_BEHAVIOR,
  'exam-question-bank': QUESTION_BANK_BEHAVIOR,
  // 批次 45（#9，issue #9）：exam 參數相依三頁（B3-2 結案；complexBody 整頁置換）。
  'exam-question-manage': {
    complexBody: (props) => <QuestionManageBody pageId={props.pageId} />,
    complexBodySubmitId: 'exam-question-manage-search-submit',
  },
  'exam-init-all-question': {
    complexBody: (props) => <InitAllQuestionBody pageId={props.pageId} />,
  },
  'exam-view-testing-view-testing': {
    complexBody: (props) => <ViewTestingBody pageId={props.pageId} />,
  },
  'exam-question-bank-type': QUESTION_BANK_TYPE_BEHAVIOR,
  'exam-exam-manage': EXAM_MANAGE_BEHAVIOR,
  'exam-dispatch-exam': EXAM_DISPATCH_BEHAVIOR,
  // 批次 14（issue #22）：report 模組 W1 匯出/download 頁群 10 頁。
  'report-common-class-form-export': CLASS_FORM_EXPORT_BEHAVIOR,
  'report-common-clazz-attendance-export': CLAZZ_ATTENDANCE_EXPORT_BEHAVIOR,
  'report-common-clazz-detail-export': CLAZZ_DETAIL_EXPORT_BEHAVIOR,
  'report-common-plan-form-export': PLAN_FORM_EXPORT_BEHAVIOR,
  'report-pgywm-all-student-scores': ALL_STUDENT_SCORES_BEHAVIOR,
  'report-tsgh-gpa-form-report': GPA_FORM_REPORT_BEHAVIOR,
  'report-ugywm-course-form-finishrate': COURSE_FORM_FINISHRATE_BEHAVIOR,
  'report-ugywm-student-dept-satisfy': STUDENT_DEPT_SATISFY_BEHAVIOR,
  'report-tsgh-core-skill-form-report': CORE_SKILL_FORM_REPORT_BEHAVIOR,
  'report-tsgh-core-skill-sutdent-report': CORE_SKILL_SUTDENT_REPORT_BEHAVIOR,
  // 批次 14 W2：查詢統計頁群 10 頁。
  'report-tsgh-login-static': LOGIN_STATIC_BEHAVIOR,
  'report-tsgh-class-form-static': CLASS_FORM_STATIC_BEHAVIOR,
  'report-tsgh-class-form-static-to-class-form-7': CLASS_FORM_STATIC_7_BEHAVIOR,
  'report-tsgh-plan-form-static': PLAN_FORM_STATIC_BEHAVIOR,
  'report-tsgh-plan-form-static-to-plan-form-5': PLAN_FORM_STATIC_5_BEHAVIOR,
  'report-tsgh-plan-form-static-2': PLAN_FORM_STATIC_2_BEHAVIOR,
  'report-tsgh-plan-form-static-2-to-plan-form-9': PLAN_FORM_STATIC_9_BEHAVIOR,
  'report-tsgh-plan-form-static-2-to-plan-form-10': PLAN_FORM_STATIC_10_BEHAVIOR,
  'report-tsgh-core-skill-report-by-form': CORE_SKILL_REPORT_BY_FORM_STAT_BEHAVIOR,
  'report-tsgh-core-skill-report-by-student': CORE_SKILL_REPORT_BY_STUDENT_STAT_BEHAVIOR,
  // 批次 14 W3：特殊頁群 3 頁。
  'report-tsgh-core-skill-statistic': CORE_SKILL_STATISTIC_BEHAVIOR,
  'report-tsgh-core-skill-statistic-report': CORE_SKILL_STATISTIC_REPORT_BEHAVIOR,
  'report-tsgh-academic-mom': ACADEMIC_MOM_BEHAVIOR,
  // 批次 15（B7-1，issue #13）：訓練期程（訓練期程大 modal＋匯入區＋明細展開＋表單明細）。
  'training-plan-inst-plan-inst-list': PLAN_INST_BEHAVIOR,
  // 批次 15 Wave 2（B7-2）：學習紀錄填寫／學習紀錄查詢。
  'training-study-record-study-record-fill': STUDY_RECORD_FILL_BEHAVIOR,
  'training-study-record-study-record-status': STUDY_RECORD_STATUS_BEHAVIOR,
  // 批次 15 Wave 3（B7-3/B7-4）：成績單／教師安排（complexBody）＋訓練計畫列連結。
  'training-transcript-list': TRANSCRIPT_BEHAVIOR,
  'training-arrange-train': ARRANGE_TRAIN_BEHAVIOR,
  'instruction-plan-target-plan-target-list': PLAN_TARGET_BEHAVIOR,
  // 批次 41（#39 修復 3/3）：form-class-form-list complexBody 化（CLASS_FORM_LIST_BEHAVIOR）；
  // form-plan-form-list 維持共用 FORM_LIST_BEHAVIOR。
  'form-class-form-list': CLASS_FORM_LIST_BEHAVIOR,
  'form-plan-form-list': FORM_LIST_BEHAVIOR,
  'form-plan-form-dispatch': FORM_DISPATCH_BEHAVIOR,
  // 批次 45（#11 B5-2，issue #11）：leave-leave-annual 設定學習年度（modal 驗證鏈接線）。
  'leave-leave-annual': LEAVE_ANNUAL_BEHAVIOR,
  'leave-leave-type': LEAVE_TYPE_BEHAVIOR,
  // leave-fix（2026-10-04）：請假申請專用 modal（淘汰 11 欄表格直映）＋送出後審核中。
  'leave-leave-form': LEAVE_FORM_BEHAVIOR,
  'announcement-announcement-list': ANNOUNCEMENT_BEHAVIOR,
  'discuss-discuss-board': DISCUSS_BOARD_BEHAVIOR,
  'discuss-discuss-type': DISCUSS_TYPE_BEHAVIOR,
  // 批次 16（B8-2，issue #15）：個人基本資料維護（complexBody 整頁置換）。
  'profile-profile-maintenance': PROFILE_BEHAVIOR,
  // 批次 17（B8-3，issue #16）：投票維護＋customer 設定頁（complexBody 整頁置換）。
  'vote-vote-maintenance': VOTE_BEHAVIOR,
  'customer-tsgh-high-risk-setting': HIGH_RISK_SETTING_BEHAVIOR,
  'customer-tsgh-service-standard-setting': SERVICE_STANDARD_SETTING_BEHAVIOR,
  'instruction-personalization-gpa-score-setting': PERSONALIZATION_GPA_SCORE_SETTING_BEHAVIOR,
  // 批次 18（issue #23）：customer 匯出型 5 頁（reportBody 整頁置換）。
  'customer-tsgh-high-risk-doctor-report': HIGH_RISK_DOCTOR_REPORT_BEHAVIOR,
  'customer-tsgh-high-risk-report': HIGH_RISK_REPORT_BEHAVIOR,
  'customer-tsgh-high-risk-setting-report': HIGH_RISK_SETTING_REPORT_BEHAVIOR,
  'customer-tsgh-service-standard-cfg-report': SERVICE_STANDARD_CFG_REPORT_BEHAVIOR,
  'customer-tsgh-service-standard-report': SERVICE_STANDARD_REPORT_BEHAVIOR,
  'customer-tsgh-tch-satisfaction-survey-report': TCH_SATISFACTION_REPORT_BEHAVIOR,
  // 批次 18 S2：查詢型 2 頁（reportBody 整頁置換）。
  'customer-tsgh-case-report': CASE_REPORT_BEHAVIOR,
  'customer-tsgh-tsgh-student-course-report': STUDENT_COURSE_REPORT_BEHAVIOR,
  // 批次 18 S3：查詢型複雜 2 頁（reportBody 整頁置換）。
  'customer-tsgh-core-curriculum-report': CORE_CURRICULUM_REPORT_BEHAVIOR,
  'customer-tsgh-course-record-report': COURSE_RECORD_REPORT_BEHAVIOR,
  // 批次 18 S4：display 2 頁（complexBody 整頁置換）。
  'customer-tsgh-clert-cerk': CLERT_CERK_BEHAVIOR,
  'customer-tsgh-tsgh-study-history-list': STUDY_HISTORY_LIST_BEHAVIOR,
  // 批次 42（GNL-005-d，issue #40 W12②）：實習證明單樣板維護（complexBody 整頁置換）。
  'customer-tsgh-cert-template-cfg': CERT_TEMPLATE_BEHAVIOR,
  // 批次 19（B8-1，issue #14）：dashboard 頁型（main-* 首頁 8 變體；契約 0 欄非契約驅動）。
  // 資料層＝dashboard-store；S1~S4 逐變體補行為（執行文件 §3）。
  'main-student-main': DASHBOARD_BEHAVIOR,
  'main-teacher-main': DASHBOARD_BEHAVIOR,
  'main-dentisit-student-main': DASHBOARD_BEHAVIOR,
  'main-dentisit-teacher-main': DASHBOARD_BEHAVIOR,
  'main-medical-affairs-student-main': DASHBOARD_BEHAVIOR,
  'main-medical-affairs-teacher-main': DASHBOARD_BEHAVIOR,
  'main-new-main-exam-list': DASHBOARD_BEHAVIOR,
  'main-new-main-study-record': DASHBOARD_BEHAVIOR,
  // 批次 20（admin 首頁專批）：main-main 角色分流（R20-1）＋main-dept-admin-main（DeptAdminBody）＋
  // main-medical-affairs-clinical-main（第 9 變體，unresolvedBody，R20-2/B19-1）。
  'main-main': MAIN_MAIN_BEHAVIOR,
  'main-dept-admin-main': DEPT_ADMIN_BEHAVIOR,
  'main-medical-affairs-clinical-main': CLINICAL_MAIN_BEHAVIOR,
  // 批次 21（Gitea issue #24，B8-3 未涵蓋）：vote 報表/投票頁群 4 頁。
  'vote-dept-manager-recommend': DEPT_MANAGER_RECOMMEND_BEHAVIOR,
  'vote-excellent-teacher': EXCELLENT_TEACHER_BEHAVIOR,
  'vote-total-vote-report': TOTAL_VOTE_REPORT_BEHAVIOR,
  'vote-total-vote-analysis-report': TOTAL_VOTE_ANALYSIS_REPORT_BEHAVIOR,
  // 批次 43（#41 GNL-005-e）：共同編輯表單查詢（complexBody 整頁置換；批次 16/41 先例；
  // 一 label 多控制項群/radio 群/無 name checkbox＝泛用渲染無法表達，緣起見 body 檔頭）。
  'form-co-edit-form-query-page': CO_EDIT_QUERY_PAGE_BEHAVIOR,
  'form-co-edit-form-fill-form': CO_EDIT_FILL_FORM_BEHAVIOR,
  'form-co-edit-form-form-generator': CO_EDIT_FORM_GENERATOR_BEHAVIOR,
};



export function pageBehavior(pageId: string): PageBehavior | undefined {
  return REGISTRY[pageId];
}
